import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import Chart from '../../components/shared/LazyChart';
import {
    RefreshCw, Key, Package, AlertTriangle,
    ShieldAlert, TrendingUp, TrendingDown, CheckCircle,
    Server, Layers, Activity, Gauge, DollarSign,
    XCircle, Cpu, RotateCcw, ArrowUpRight, ArrowDownRight,
    Download, LayoutGrid, Info, Percent, Cloud,
    ShieldCheck, Clock, ServerCog,
} from 'lucide-react';
import {
    dashboardService,
    DashboardFilters,
    GlobalSummary,
    AssetStats,
    LicenseStats,
    InventoryKpis,
    AssetFinancialKpis,
    DashboardAlerts,
    AssetLifecycleKpis,
    AssetDistributions,
    AssetFacts,
    LicenseCostKpis,
    LicenseDistributions,
    LicenseFacts,
    InventoryActivityKpis,
    KpiAvailability,
} from '../../services/dashboardService';
import {
    aggregate as aggregateLicenses,
    toggleSelection as toggleLicenseSelection,
    isSelected as isLicenseSelected,
    labelForKey as labelForLicenseKey,
    applySelection as applySelectionToFacts,
    selectionCount,
    LicenseDimension, LicenseSelection, LICENSE_DIMENSION_LABELS,
    LicenseAnalyticsBreakdownRow,
} from './licenseAnalyticsModel';
import {
    aggregate as aggregateAssets,
    toggleSelection as toggleAssetSelection,
    isSelected as isAssetSelected,
    labelForKey as labelForAssetKey,
    AssetDimension, AssetSelection, ASSET_DIMENSION_LABELS,
    AssetAnalyticsBreakdownRow,
} from './assetAnalyticsModel';
import {
    analyticsSnapshotService,
    SnapshotComparisonResult,
    SnapshotJobHealth,
    AnalyticsSnapshotDomain,
} from '../../services/analyticsSnapshotService';
import { masterService, Department, Location, Brand, Vendor } from '../../services/masterService';
import { categoryService, Category } from '../../services/categoryService';
import { useToast } from '../../context/ToastContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useAuth } from '../../hooks/useAuth';
import { PageHeader } from '../../components/shared/PageHeader';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { cn } from '../../lib/utils';
import { getChartSeries, getChartNeutrals, isDarkTheme } from '../../lib/chartColors';
import { formatPercent } from '../../lib/formatNumber';
import { exportToCsv } from '../../lib/exportCsv';
import { KpiDetailModal } from '../Dashboard/KpiDetailModal';
import { useNoWheelHijack } from '../../hooks/useNoWheelHijack';
import {
    num, pct, fmt, KpiCard, SectionHead, ProgressRow, EmptyStateCard, FocusableVisual,
    KPI_GRID, CONTENT_GRID, COL_PRIMARY, COL_SECONDARY, chartFont, varianceBarOptions, deltaBarOptions,
} from './analyticsPrimitives';
import { CloudAnalyticsPanel } from './CloudAnalyticsPanel';
import { CloudAnalytics } from '../../services/cloudAssetService';
import { FilterButton } from '../../components/shared/FilterButton';
import { FilterChipsBar, FilterChip } from '../../components/shared/FilterChipsBar';
import {
    AnalyticsAdvancedFilters, AnalyticsFilterState, EMPTY_ANALYTICS_FILTERS,
    TAB_FILTER_FIELDS, countActiveAnalyticsFilters,
} from './AnalyticsAdvancedFilters';

interface StockMovementData {
    thisMonth: { reason: string; count: string }[];
    lastMonth: { reason: string; count: string }[];
}

interface LicenseUtilizationRow {
    license_softwareName: string;
    license_totalSeats: number;
    license_usedSeats: number;
}

type TabKey = 'executive' | 'assets' | 'licenses' | 'inventory' | 'cloud';

/* Story 104 — turns the backend's 'YYYY-MM' trend key into a short axis label
   ("2026-08" → "Aug 26"). Parsed as a plain string rather than a Date so the
   month never shifts across a timezone boundary. */
const MONTH_ABBREVIATIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatMonthLabel(month: string): string {
    const [year, monthNumber] = month.split('-');
    const abbreviation = MONTH_ABBREVIATIONS[Number(monthNumber) - 1];
    return abbreviation ? `${abbreviation} ${year.slice(2)}` : month;
}

/* Aligns a set of monthly series onto one shared, gap-free month axis. Postgres
   returns only months that actually have rows, so two series (repairs started
   vs completed) can otherwise disagree on what point 3 means. */
function alignTrendMonths(...series: { month: string; count: number }[][]): {
    months: string[];
    values: number[][];
} {
    const months = [...new Set(series.flatMap(s => s.map(p => p.month)))].sort();
    return {
        months,
        values: series.map(s => months.map(m => s.find(p => p.month === m)?.count ?? 0)),
    };
}

/* ══════════════════════ sub-components ══════════════════════ */

/* Trend / comparison badge — derived from a snapshot compare() result for one metric.
   Renders an explicit unavailable message (not a fabricated 0% or a hidden card) whenever the
   backing snapshot data doesn't exist yet — see docs/ANALYTICS_IMPLEMENTATION_PLAN.md §7.3.
   `comparison === undefined` means the fetch itself hasn't resolved/failed (network error,
   still loading); `comparison.available === false` means the fetch succeeded but there isn't
   yet a second ACTUAL snapshot ~7 days back to compare against — the expected state for the
   first week after this feature is enabled, not a defect, so it gets its own wording. */
const TrendNote: React.FC<{ comparison?: SnapshotComparisonResult; metricKey: string; periodLabel?: string }> = ({ comparison, metricKey, periodLabel = 'week' }) => {
    if (!comparison) {
        return <p className="analytics-kpi-sub text-muted-foreground analytics-trend-unavailable">Comparison unavailable</p>;
    }
    if (!comparison.available) {
        return <p className="analytics-kpi-sub text-muted-foreground analytics-trend-unavailable">Available after 7 days of snapshot history</p>;
    }
    const metric = comparison.metrics?.[metricKey];
    if (!metric || Array.isArray(metric) || typeof (metric as { current?: unknown }).current !== 'number') {
        return <p className="analytics-kpi-sub text-muted-foreground analytics-trend-unavailable">Comparison unavailable</p>;
    }
    const m = metric as { percentChange: number | null };
    if (m.percentChange === null) {
        return <p className="analytics-kpi-sub text-muted-foreground">vs last {periodLabel} · n/a (was 0)</p>;
    }
    const up = m.percentChange >= 0;
    return (
        <p className={cn('analytics-kpi-sub analytics-trend-inline', up ? 'text-success' : 'text-destructive')}>
            {up ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
            {formatPercent(Math.abs(m.percentChange))} vs last {periodLabel}
        </p>
    );
};

/* Snapshot job health strip — ops-only (gated on 'analytics.snapshot.manage', same as the
   backing /snapshot/health endpoint). Reads whether each domain's daily capture is actually
   landing rows in analytics_daily_snapshot, not just whether the last cron run reported
   "success" — a run can report success while the row it wrote is later removed (e.g. a
   retention purge or a schema reset), so per-domain status is judged off
   lastSuccessfulSnapshotDate, the persisted source of truth. */
const SNAPSHOT_DOMAIN_LABELS: Record<AnalyticsSnapshotDomain, string> = {
    asset: 'Asset', license: 'License', inventory: 'Inventory',
};
function daysSince(dateStr: string | null): number | null {
    if (!dateStr) return null;
    const ms = Date.now() - new Date(`${dateStr}T00:00:00Z`).getTime();
    return Math.floor(ms / 86400000);
}
const SnapshotHealthStrip: React.FC<{ health: SnapshotJobHealth }> = ({ health }) => (
    <div className="mt-4 pt-4 border-t border-border">
        <p className="text-xs font-medium text-muted-foreground mb-2">Daily snapshot capture health</p>
        <div className="flex flex-wrap gap-4">
            {(Object.keys(SNAPSHOT_DOMAIN_LABELS) as AnalyticsSnapshotDomain[]).map((domain) => {
                const lastDate = health.perDomain[domain]?.lastSuccessfulSnapshotDate ?? null;
                const age = daysSince(lastDate);
                const failedToday = health.lastAttempt?.failedDomains?.some((f) => f.domain === domain) ?? false;
                const status: { icon: React.ReactNode; label: string; className: string } =
                    lastDate === null
                        ? { icon: <XCircle size={13} />, label: 'Never captured', className: 'text-destructive' }
                        : failedToday
                            ? { icon: <AlertTriangle size={13} />, label: `Last attempt failed · latest good: ${lastDate}`, className: 'text-warning' }
                            : (age ?? 0) > 1
                                ? { icon: <AlertTriangle size={13} />, label: `Stale since ${lastDate}`, className: 'text-warning' }
                                : { icon: <CheckCircle size={13} />, label: `Capturing · latest ${lastDate}`, className: 'text-success' };
                return (
                    <div key={domain} className={cn('flex items-center gap-1.5 text-xs', status.className)}>
                        {status.icon}
                        <span className="font-medium text-foreground">{SNAPSHOT_DOMAIN_LABELS[domain]}:</span>
                        <span>{status.label}</span>
                    </div>
                );
            })}
        </div>
    </div>
);

/* Formats a KpiAvailability<number> — shows the value, or an explicit "Not
   available: <reason>" note rather than a fabricated 0/blank when the backend
   couldn't reliably compute it (zero denominator, deferred business decision). */
function formatAvailability(a: KpiAvailability<number> | undefined, formatter: (n: number) => string): { display: string; unavailable: boolean; reason?: string } {
    if (!a || !a.available || a.value === null) {
        return { display: 'Not available', unavailable: true, reason: a?.reason };
    }
    return { display: formatter(a.value), unavailable: false };
}

/* Honest gaps — same pattern as CloudAnalyticsPanel's "Not available from this
   data" card, brought to Assets/Licenses. Names a chart that can't be built
   with its cause (a data-model limit, documented in USER_STORIES.md Story
   104/105 "Out of scope") rather than just never showing it, so a missing
   chart reads as a known limitation instead of an unfinished screen. The list
   is static — these are schema gaps, not something a fetch can discover. */
interface UnavailableMetric { id: string; label: string; reason: string }
const UnavailableMetricsCard: React.FC<{ metrics: UnavailableMetric[] }> = ({ metrics }) => (
    <Card>
        <CardContent className="p-5">
            <SectionHead title="Not available from this data" note="Charts that need fields this register does not hold" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                {metrics.map((metric) => (
                    <div key={metric.id} className="flex gap-2.5 rounded-md border border-dashed border-border p-3">
                        <ServerCog size={16} className="mt-0.5 shrink-0 text-muted-foreground/60" />
                        <div>
                            <p className="text-sm font-medium text-foreground">{metric.label}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{metric.reason}</p>
                        </div>
                    </div>
                ))}
            </div>
        </CardContent>
    </Card>
);

const ASSET_UNAVAILABLE_METRICS: UnavailableMetric[] = [
    { id: 'assets-by-os', label: 'Assets by Operating System', reason: 'No column records the OS of an asset.' },
    { id: 'maintenance-cost-trend', label: 'Maintenance Cost Trend', reason: 'Maintenance spend is not tracked per event.' },
    { id: 'top-maintenance-cost', label: 'Top Assets by Maintenance Cost', reason: 'Maintenance spend is not tracked per event.' },
    { id: 'security-issues', label: 'Assets with Security Issues', reason: 'No vulnerability/security data is captured for assets.' },
    { id: 'idle-unused', label: 'Idle / Unused Assets', reason: 'No usage/activity signal exists to define "idle".' },
    { id: 'depreciation-trend', label: 'Depreciation Trend', reason: 'No agreed depreciation method/schedule yet.' },
    { id: 'compliant-noncompliant', label: 'Compliant vs Non-Compliant Assets', reason: 'No agreed definition of "compliant" for hardware yet.' },
];

const LICENSE_UNAVAILABLE_METRICS: UnavailableMetric[] = [
    { id: 'by-status-suspended', label: 'Licenses by Status (incl. Suspended)', reason: 'No "Suspended" state exists — only derivable Active/Expired/Expiring.' },
    { id: 'subscription-perpetual', label: 'License Type: Subscription vs Perpetual', reason: 'Only User-based/Device-based type is recorded, not billing model.' },
    { id: 'by-department', label: 'Licenses / Cost by Department or Business Unit', reason: 'No department/business-unit column exists on a license.' },
    { id: 'used-vs-unused-login', label: 'Used vs Unused (by login activity)', reason: 'No login/usage tracking exists — only seat assignment.' },
    { id: 'savings-opportunities', label: 'License Savings Opportunities', reason: 'No recommendation logic exists yet beyond raw unused-cost/over-allocation figures.' },
    { id: 'compliant-noncompliant', label: 'Compliant vs Non-Compliant Licenses', reason: 'complianceRisk is a free-text risk level, not a compliant/non-compliant flag.' },
    { id: 'allocation-by-device', label: 'License Allocation by Device', reason: 'License assignments link to a user only, never to a device/asset.' },
    { id: 'auto-manual-renewal', label: 'Auto-Renewal vs Manual Renewal', reason: 'No autoRenewal field exists on a license.' },
    { id: 'renewal-cost-forecast', label: 'License Renewal Cost Forecast', reason: 'Renewal cost history exists, but no forecasting model is implemented.' },
];

/* Risk queue row — one actionable exception count, reusing the same kpiId drill-through
   ids already established elsewhere in this file/backend so "View" opens real detail. */
interface RiskRowProps {
    label: string;
    value: number;
    severity: 'critical' | 'warning' | 'info';
    kpiId?: string;
    onClick?: (kpiId: string) => void;
}
const RiskRow: React.FC<RiskRowProps> = ({ label, value, severity, kpiId, onClick }) => {
    if (value <= 0) return null;
    const clickable = !!(kpiId && onClick);
    return (
        <TableRow className={cn(clickable && 'cursor-pointer hover:bg-muted/40')} onClick={clickable ? () => onClick!(kpiId!) : undefined}>
            <TableCell>
                <span className={`risk-dot risk-${severity}`} />
                {label}
            </TableCell>
            <TableCell className="tabular-nums font-semibold text-foreground">{fmt(value)}</TableCell>
            <TableCell className="text-muted-foreground text-xs">{clickable ? 'Click to view records' : '—'}</TableCell>
        </TableRow>
    );
};

/* ══════════════════════ MAIN COMPONENT ══════════════════════ */
const AnalyticsDashboard: React.FC = () => {
    // Re-evaluated each render; AppShell remounts routed pages on theme toggle
    const CHART_COLORS = getChartSeries();
    const CHART_INK = getChartNeutrals();
    const CHART_TOOLTIP_THEME = isDarkTheme() ? 'dark' : 'light';
    const { showToast } = useToast();
    const { formatDisplayAmount, currencyCode } = useCurrency();
    const { hasPermission } = useAuth();
    const tabsRef = useNoWheelHijack<HTMLDivElement>();

    // Phase 4 — per-tab permissions (docs/ANALYTICS_IMPLEMENTATION_PLAN.md §7.6).
    // The backend independently enforces every one of these on the matching
    // endpoint; this is UX only (hiding what a 403 would reject anyway).
    const canView = useMemo(() => ({
        executive: hasPermission('analytics.executive.view'),
        assets: hasPermission('analytics.assets.view'),
        licenses: hasPermission('analytics.licenses.view'),
        inventory: hasPermission('analytics.inventory.view'),
        cloud: hasPermission('analytics.cloud.view'),
    }), [hasPermission]);
    const canViewFinancial = hasPermission('analytics.financial.view');
    // Same permission the backend's /snapshot/health endpoint requires — "operate the
    // snapshot job", not "view analytics data" (see AnalyticsSnapshotController).
    const canManageSnapshots = hasPermission('analytics.snapshot.manage');
    const hasAnyAnalyticsTab = canView.executive || canView.assets || canView.licenses || canView.inventory || canView.cloud;

    const [activeTab, setActiveTab] = useState<TabKey>(() => {
        const order: TabKey[] = ['executive', 'assets', 'licenses', 'inventory', 'cloud'];
        return order.find((t) => canView[t]) ?? 'executive';
    });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

    const [masters, setMasters] = useState<{
        departments: Department[]; locations: Location[]; categories: Category[];
        brands: Brand[]; vendors: Vendor[];
    }>({ departments: [], locations: [], categories: [], brands: [], vendors: [] });

    /* ── filters (Story 101) ──
       Held per tab, not globally: each tab offers only the filters its own
       endpoints honour, so carrying a licence filter onto Inventory would
       show an applied chip that changes nothing. The Cloud tab filters
       client-side against its fact table and is not represented here. */
    const [filtersByTab, setFiltersByTab] = useState<Record<string, AnalyticsFilterState>>({});
    const [filterDraft, setFilterDraft] = useState<AnalyticsFilterState>(EMPTY_ANALYTICS_FILTERS);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    const tabFilters = filtersByTab[activeTab] ?? EMPTY_ANALYTICS_FILTERS;
    const tabFilterFields = TAB_FILTER_FIELDS[activeTab] ?? [];
    const activeFilterCount = countActiveAnalyticsFilters(tabFilters, tabFilterFields);

    const setTabFilters = (next: AnalyticsFilterState) =>
        setFiltersByTab((current) => ({ ...current, [activeTab]: next }));
    const clearFilters = () => {
        setTabFilters(EMPTY_ANALYTICS_FILTERS);
        setFilterDraft(EMPTY_ANALYTICS_FILTERS);
    };
    const openFilterDrawer = () => {
        // The drawer edits a draft; nothing reaches the charts until Apply.
        setFilterDraft(tabFilters);
        setFilterDrawerOpen(true);
    };

    // A date range needs both ends to mean anything to the API, so a half-set
    // range is deliberately not sent.
    const activeFilters: DashboardFilters = useMemo(() => {
        const f = filtersByTab[activeTab] ?? EMPTY_ANALYTICS_FILTERS;
        const fields = TAB_FILTER_FIELDS[activeTab] ?? [];
        const out: DashboardFilters = {};
        if (fields.includes('dateRange') && f.startDate && f.endDate) {
            out.startDate = f.startDate;
            out.endDate = f.endDate;
        }
        (['departmentId', 'locationId', 'categoryId', 'brandId', 'vendorId'] as const).forEach((key) => {
            if (fields.includes(key) && f[key] !== undefined) out[key] = f[key];
        });
        if (fields.includes('status') && f.status) out.status = f.status;
        return out;
    }, [filtersByTab, activeTab]);

    const filterChips: FilterChip[] = useMemo(() => {
        const f = tabFilters;
        const chips: FilterChip[] = [];
        const nameOf = (list: { id: number; name: string }[], id?: number) =>
            list.find((o) => o.id === id)?.name ?? String(id ?? '');
        if (tabFilterFields.includes('dateRange') && (f.startDate || f.endDate)) {
            chips.push({
                key: 'dateRange',
                label: `Period: ${f.startDate || '…'} → ${f.endDate || '…'}`,
                onRemove: () => setTabFilters({ ...f, startDate: undefined, endDate: undefined }),
            });
        }
        const idChip = (
            key: 'departmentId' | 'locationId' | 'categoryId' | 'brandId' | 'vendorId',
            label: string, list: { id: number; name: string }[],
        ) => {
            if (!tabFilterFields.includes(key) || f[key] === undefined) return;
            chips.push({
                key, label: `${label}: ${nameOf(list, f[key])}`,
                onRemove: () => setTabFilters({ ...f, [key]: undefined }),
            });
        };
        idChip('categoryId', 'Category', masters.categories as any);
        idChip('departmentId', 'Department', masters.departments as any);
        idChip('locationId', 'Location', masters.locations as any);
        idChip('brandId', 'Brand', masters.brands as any);
        idChip('vendorId', 'Vendor', masters.vendors as any);
        if (tabFilterFields.includes('status') && f.status) {
            chips.push({
                key: 'status', label: `Status: ${f.status}`,
                onRemove: () => setTabFilters({ ...f, status: undefined }),
            });
        }
        return chips;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabFilters, tabFilterFields, masters]);

    /* ── KPI drill-down ── */
    const [activeKpiId, setActiveKpiId] = useState<string | null>(null);
    // Only meaningful when activeKpiId === 'kpi-warranty-status' — scopes that
    // popup to the specific tier the user clicked instead of the combined view.
    const [warrantyBucket, setWarrantyBucket] = useState<'expired' | '30' | '60' | '90' | undefined>(undefined);
    const openWarrantyDrilldown = (b: 'expired' | '60' | '30') => { setWarrantyBucket(b); setActiveKpiId('kpi-warranty-status'); };
    // Only meaningful when activeKpiId === 'kpi-useful-life' — scopes that
    // popup to the specific tier the user clicked instead of the combined view.
    const [usefulLifeBucket, setUsefulLifeBucket] = useState<'near' | 'beyond' | undefined>(undefined);
    const openUsefulLifeDrilldown = (b: 'near' | 'beyond') => { setUsefulLifeBucket(b); setActiveKpiId('kpi-useful-life'); };

    const [global, setGlobal] = useState<GlobalSummary | null>(null);
    const [assets, setAssets] = useState<AssetStats | null>(null);
    const [licenses, setLicenses] = useState<LicenseStats | null>(null);
    const [inventory, setInventory] = useState<InventoryKpis | null>(null);
    const [stockMovement, setStockMovement] = useState<StockMovementData>({ thisMonth: [], lastMonth: [] });
    const [licenseUtil, setLicenseUtil] = useState<LicenseUtilizationRow[]>([]);

    /* ── Executive tab — extra data + week-over-week trend comparisons ── */
    const [assetFinancial, setAssetFinancial] = useState<AssetFinancialKpis | null>(null);
    const [alerts, setAlerts] = useState<DashboardAlerts | null>(null);
    const [trends, setTrends] = useState<Partial<Record<AnalyticsSnapshotDomain, SnapshotComparisonResult>>>({});
    const [snapshotHealth, setSnapshotHealth] = useState<SnapshotJobHealth | null>(null);
    const [execLoading, setExecLoading] = useState(false);
    const [execPartialError, setExecPartialError] = useState<string | null>(null);
    const [execRefreshTick, setExecRefreshTick] = useState(0);

    /* ── Phase 3 — remaining Asset/License/Inventory KPIs, lazily loaded per tab ── */
    const [assetLifecycle, setAssetLifecycle] = useState<AssetLifecycleKpis | null>(null);
    const [assetLifecycleLoading, setAssetLifecycleLoading] = useState(false);
    /* Story 104 — the "Assets by X" breakdowns; fetched separately from the
       lifecycle KPIs so one failing endpoint doesn't blank the other's charts. */
    const [assetDistributions, setAssetDistributions] = useState<AssetDistributions | null>(null);
    const [assetDistributionsLoading, setAssetDistributionsLoading] = useState(false);
    /* Story 108 — one fact row per asset, for click-to-filter cross-filtering
       on the Assets tab (mirrors the Licences tab's Story 107). Fetched
       separately again, same reasoning — one endpoint's failure should not
       blank the rest of the tab. */
    const [assetFacts, setAssetFacts] = useState<AssetFacts | null>(null);
    const [assetFactsLoading, setAssetFactsLoading] = useState(false);
    const [assetSelection, setAssetSelection] = useState<AssetSelection>({});
    const [licenseCost, setLicenseCost] = useState<LicenseCostKpis | null>(null);
    const [licenseCostLoading, setLicenseCostLoading] = useState(false);
    /* Reader-controlled depth for "Top Expensive Licenses" — a ranked report,
       not a chart, so how far down the reader wants to look is their call. */
    const [topExpensiveLimit, setTopExpensiveLimit] = useState(5);
    /* Story 105 — the "Licenses by X" breakdowns; fetched separately from the
       cost KPIs so one failing endpoint doesn't blank the other's charts. */
    const [licenseDistributions, setLicenseDistributions] = useState<LicenseDistributions | null>(null);
    const [licenseDistributionsLoading, setLicenseDistributionsLoading] = useState(false);
    /* Story 107 — one fact row per license, for click-to-filter cross-filtering
       on the Licences tab (mirrors the Cloud tab's Story 100). Fetched
       separately again, same reasoning — one endpoint's failure should not
       blank the rest of the tab. */
    const [licenseFacts, setLicenseFacts] = useState<LicenseFacts | null>(null);
    const [licenseFactsLoading, setLicenseFactsLoading] = useState(false);
    const [licenseSelection, setLicenseSelection] = useState<LicenseSelection>({});
    /* The cost/compliance panel (getLicenseCostKpis) is fetched separately
       from the fact table and deliberately carries no cost fields there —
       see licenseIds' doc comment on the backend DashboardFilters — so it
       cannot recompute from a selection client-side the way the non-financial
       charts do. Instead the selection is resolved to a concrete license id
       list here and sent as a request param, and the whole cost panel
       (including "Annualized Cost by Vendor") is refetched under that scope. */
    const licenseSelectionIds = useMemo(
        () => (
            licenseFacts && !licenseFacts.factsTruncated && selectionCount(licenseSelection) > 0
                ? applySelectionToFacts(licenseFacts.facts, licenseSelection).map(f => f.id)
                : undefined
        ),
        [licenseFacts, licenseSelection],
    );
    const [inventoryActivity, setInventoryActivity] = useState<InventoryActivityKpis | null>(null);
    const [inventoryActivityLoading, setInventoryActivityLoading] = useState(false);
    const [phase3RefreshTick, setPhase3RefreshTick] = useState(0);
    /* ── Cloud tab (Story 98) — self-fetching panel, re-run by this tick. Its
       payload is mirrored up here only so the page's Export CSV button has
       something to flatten on this tab instead of silently doing nothing. ── */
    const [cloudRefreshTick, setCloudRefreshTick] = useState(0);
    const [cloudAnalytics, setCloudAnalytics] = useState<CloudAnalytics | null>(null);
    /* The Cloud panel's Filters button, hoisted into the page header so this tab
       carries its control in the same place as every other tab. */
    const [cloudFilterControl, setCloudFilterControl] =
        useState<{ activeCount: number; open: () => void } | null>(null);

    /* ── load masters ── */
    useEffect(() => {
        masterService.getDepartments().then(d => setMasters(m => ({ ...m, departments: d }))).catch(() => { });
        masterService.getLocations().then(d => setMasters(m => ({ ...m, locations: d }))).catch(() => { });
        masterService.getBrands().then(d => setMasters(m => ({ ...m, brands: d }))).catch(() => { });
        masterService.getVendors().then(d => setMasters(m => ({ ...m, vendors: d }))).catch(() => { });
        categoryService.getCategories().then(d => setMasters(m => ({ ...m, categories: d }))).catch(() => { });
    }, []);

    /* ── load data ──
       Every fetch effect below follows the same cancellation pattern: an
       AbortController per invocation (aborted on cleanup — filter/tab/date/
       currency change or unmount), plus the pre-existing `cancelled` closure
       flag as a belt-and-braces guard against a stale response overwriting
       newer state. Aborted requests reject with a cancel error, which every
       catch/allSettled branch below recognizes and treats as "ignore", never
       as a user-facing failure. See docs/ANALYTICS_IMPLEMENTATION_PLAN.md §7.7. */
    const loadAllAbortRef = useRef<AbortController | null>(null);
    const loadAll = useCallback(async (f: DashboardFilters) => {
        loadAllAbortRef.current?.abort();
        const controller = new AbortController();
        loadAllAbortRef.current = controller;
        setRefreshing(true);
        try {
            // Monetary aggregates are converted server-side into the user's display currency
            f = { ...(f || {}), displayCurrency: currencyCode };
            const [g, a, lic, inv, sm, lu] = await Promise.all([
                dashboardService.getGlobalSummary(f || {}, controller.signal),
                dashboardService.getAssetStats(f || {}, controller.signal),
                dashboardService.getLicenseStats(f || {}, controller.signal),
                dashboardService.getInventoryKpis(f || {}, controller.signal),
                dashboardService.getStockMovement(f || {}, controller.signal),
                dashboardService.getLicenseUtilization(f || {}, controller.signal),
            ]);
            setGlobal(g);
            setAssets(a);
            setLicenses(lic);
            setInventory(inv);
            setStockMovement(sm || { thisMonth: [], lastMonth: [] });
            setLicenseUtil(lu || []);
            setLastUpdated(new Date());
        } catch (err: unknown) {
            if (axios.isCancel(err)) return; // superseded by a newer request or the component unmounted — not a real failure
            const error = err as { response?: { data?: { message?: string } } };
            showToast(error?.response?.data?.message || 'Failed to load analytics', 'error');
        } finally {
            // Only the still-current (latest) request may resolve the loading
            // state — an aborted older call's finally must not flip
            // loading/refreshing back off behind the request that superseded it.
            if (loadAllAbortRef.current === controller) {
                setLoading(false);
                setRefreshing(false);
            }
        }
    }, [showToast, currencyCode]);

    useEffect(() => {
        loadAll(activeFilters);
        return () => { loadAllAbortRef.current?.abort(); };
    }, [loadAll, activeFilters]);

    /* ── Executive tab data — loaded lazily (only once the tab is opened) and
       independently of the other tabs' Promise.all above: each call is
       allSettled so one denied/failing endpoint (e.g. a role without
       dashboard.view.all) degrades to a partial-data state instead of
       blanking the whole tab. ── */
    useEffect(() => {
        if (activeTab !== 'executive') return undefined;
        let cancelled = false;
        const controller = new AbortController();
        setExecLoading(true);
        (async () => {
            // Monetary aggregates (book value, purchase cost, MRC) are converted
            // server-side into the user's display currency.
            const f = { ...activeFilters, displayCurrency: currencyCode };
            // Skip the call entirely (not just hide the result) when the user
            // lacks analytics.financial.view — avoids an expected-403 network
            // error and an unrelated "some metrics failed" banner for what is
            // actually a permission boundary, not a failure.
            const [financialRes, alertsRes, assetTrendRes, licenseTrendRes, inventoryTrendRes, healthRes] = await Promise.allSettled([
                canViewFinancial ? dashboardService.getAssetFinancialKpis(f, controller.signal) : Promise.resolve(null),
                dashboardService.getAlerts(f, controller.signal),
                analyticsSnapshotService.compareToDaysAgo('asset', 7, controller.signal),
                analyticsSnapshotService.compareToDaysAgo('license', 7, controller.signal),
                analyticsSnapshotService.compareToDaysAgo('inventory', 7, controller.signal),
                // Ops-only: 'analytics.snapshot.manage' gate mirrors the backend's
                // permission on this endpoint, so skip the call entirely for users
                // who'd just get a 403.
                canManageSnapshots ? analyticsSnapshotService.getHealth(controller.signal) : Promise.resolve(null),
            ]);
            if (cancelled) return; // a newer filter/tab change (or unmount) superseded this request — never apply stale results

            setAssetFinancial(financialRes.status === 'fulfilled' ? financialRes.value : null);
            setAlerts(alertsRes.status === 'fulfilled' ? alertsRes.value : null);
            setSnapshotHealth(healthRes.status === 'fulfilled' ? healthRes.value : null);

            const nextTrends: Partial<Record<AnalyticsSnapshotDomain, SnapshotComparisonResult>> = {};
            if (assetTrendRes.status === 'fulfilled') nextTrends.asset = assetTrendRes.value;
            if (licenseTrendRes.status === 'fulfilled') nextTrends.license = licenseTrendRes.value;
            if (inventoryTrendRes.status === 'fulfilled') nextTrends.inventory = inventoryTrendRes.value;
            setTrends(nextTrends);

            const failedCount = [alertsRes, ...(canViewFinancial ? [financialRes] : [])].filter(r => r.status === 'rejected').length;
            setExecPartialError(failedCount > 0 ? 'Some executive metrics could not be loaded — showing what is available.' : null);
            setExecLoading(false);
        })();
        return () => { cancelled = true; controller.abort(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, activeFilters, execRefreshTick, currencyCode]);

    /* ── Phase 3 — Asset lifecycle KPIs (#14, #20, #24, #26), lazily loaded ── */
    useEffect(() => {
        if (activeTab !== 'assets') return undefined;
        let cancelled = false;
        const controller = new AbortController();
        setAssetLifecycleLoading(true);
        dashboardService.getAssetLifecycleKpis({ ...activeFilters, displayCurrency: currencyCode }, controller.signal)
            .then((data) => { if (!cancelled) setAssetLifecycle(data); })
            .catch((err) => { if (!cancelled && !axios.isCancel(err)) setAssetLifecycle(null); })
            .finally(() => { if (!cancelled) setAssetLifecycleLoading(false); });
        return () => { cancelled = true; controller.abort(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, activeFilters, phase3RefreshTick, currencyCode]);

    /* ── Story 104 — Asset distribution breakdowns, lazily loaded with the Assets tab ── */
    useEffect(() => {
        if (activeTab !== 'assets') return undefined;
        let cancelled = false;
        const controller = new AbortController();
        setAssetDistributionsLoading(true);
        dashboardService.getAssetDistributions({ ...activeFilters, displayCurrency: currencyCode }, controller.signal)
            .then((data) => { if (!cancelled) setAssetDistributions(data); })
            .catch((err) => { if (!cancelled && !axios.isCancel(err)) setAssetDistributions(null); })
            .finally(() => { if (!cancelled) setAssetDistributionsLoading(false); });
        return () => { cancelled = true; controller.abort(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, activeFilters, phase3RefreshTick, currencyCode]);

    /* ── Story 108 — Asset facts, lazily loaded with the Assets tab.
       A refetch (filters/refresh changed) can retire the very assets a
       selection referred to, so the selection is cleared on every fresh load
       — same reasoning as the Licences tab's own fact refetch. ── */
    useEffect(() => {
        if (activeTab !== 'assets') return undefined;
        let cancelled = false;
        const controller = new AbortController();
        setAssetFactsLoading(true);
        dashboardService.getAssetFacts({ ...activeFilters }, controller.signal)
            .then((data) => { if (!cancelled) { setAssetFacts(data); setAssetSelection({}); } })
            .catch((err) => { if (!cancelled && !axios.isCancel(err)) setAssetFacts(null); })
            .finally(() => { if (!cancelled) setAssetFactsLoading(false); });
        return () => { cancelled = true; controller.abort(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, activeFilters, phase3RefreshTick]);

    /* ── Phase 3 — License cost/compliance KPIs (#35, #36, #39, #40), lazily loaded.
       Refetches whenever the tab's cross-filter selection changes, scoped by
       licenseSelectionIds — see its declaration for why this can't just
       recompute from the fact table client-side like the other charts. ── */
    useEffect(() => {
        if (activeTab !== 'licenses' || !canViewFinancial) return undefined;
        let cancelled = false;
        const controller = new AbortController();
        setLicenseCostLoading(true);
        dashboardService.getLicenseCostKpis(
            { ...activeFilters, displayCurrency: currencyCode, topExpensiveLimit, licenseIds: licenseSelectionIds },
            controller.signal,
        )
            .then((data) => { if (!cancelled) setLicenseCost(data); })
            .catch((err) => { if (!cancelled && !axios.isCancel(err)) setLicenseCost(null); })
            .finally(() => { if (!cancelled) setLicenseCostLoading(false); });
        return () => { cancelled = true; controller.abort(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, activeFilters, phase3RefreshTick, currencyCode, topExpensiveLimit, licenseSelectionIds]);

    /* ── Story 105 — License distribution breakdowns, lazily loaded with the Licenses tab ── */
    useEffect(() => {
        if (activeTab !== 'licenses') return undefined;
        let cancelled = false;
        const controller = new AbortController();
        setLicenseDistributionsLoading(true);
        dashboardService.getLicenseDistributions({ ...activeFilters, displayCurrency: currencyCode }, controller.signal)
            .then((data) => { if (!cancelled) setLicenseDistributions(data); })
            .catch((err) => { if (!cancelled && !axios.isCancel(err)) setLicenseDistributions(null); })
            .finally(() => { if (!cancelled) setLicenseDistributionsLoading(false); });
        return () => { cancelled = true; controller.abort(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, activeFilters, phase3RefreshTick, currencyCode]);

    /* ── Story 107 — License facts, lazily loaded with the Licenses tab.
       A refetch (filters/refresh changed) can retire the very licenses a
       selection referred to, so the selection is cleared on every fresh load
       — same reasoning as the Cloud panel's own fact refetch. ── */
    useEffect(() => {
        if (activeTab !== 'licenses') return undefined;
        let cancelled = false;
        const controller = new AbortController();
        setLicenseFactsLoading(true);
        dashboardService.getLicenseFacts({ ...activeFilters }, controller.signal)
            .then((data) => { if (!cancelled) { setLicenseFacts(data); setLicenseSelection({}); } })
            .catch((err) => { if (!cancelled && !axios.isCancel(err)) setLicenseFacts(null); })
            .finally(() => { if (!cancelled) setLicenseFactsLoading(false); });
        return () => { cancelled = true; controller.abort(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, activeFilters, phase3RefreshTick]);

    /* ── Phase 3 — Inventory activity KPIs (#49, #50), lazily loaded ── */
    useEffect(() => {
        if (activeTab !== 'inventory') return undefined;
        let cancelled = false;
        const controller = new AbortController();
        setInventoryActivityLoading(true);
        dashboardService.getInventoryActivityKpis({ ...activeFilters, displayCurrency: currencyCode }, controller.signal)
            .then((data) => { if (!cancelled) setInventoryActivity(data); })
            .catch((err) => { if (!cancelled && !axios.isCancel(err)) setInventoryActivity(null); })
            .finally(() => { if (!cancelled) setInventoryActivityLoading(false); });
        return () => { cancelled = true; controller.abort(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, activeFilters, phase3RefreshTick, currencyCode]);

    /* ══════ DERIVED CHART DATA ══════ */

    /* Asset donut */
    const assetStatusSeries = (assets?.byStatus || []).map(s => parseInt(s.count));
    const assetStatusLabels = (assets?.byStatus || []).map(s => s.status.charAt(0).toUpperCase() + s.status.slice(1));

    /* Asset by category bar */
    const catLabels = (assets?.byCategory || []).map(c => c.category || 'Unknown');
    const catSeries = (assets?.byCategory || []).map(c => parseInt(c.count));

    /* Stock movement */
    const movKeys = ['procurement', 'issue', 'return', 'adjustment'];
    const movLabels = ['Procurement', 'Issue', 'Return', 'Adjustment'];
    interface StockMovementItem { reason: string; count: string; }
    const smThis = movKeys.map(r => parseInt(stockMovement.thisMonth.find((m: StockMovementItem) => m.reason === r)?.count || '0'));
    const smLast = movKeys.map(r => parseInt(stockMovement.lastMonth.find((m: StockMovementItem) => m.reason === r)?.count || '0'));

    /* Inventory donut */
    const refundable = num(inventory?.distribution?.refundable);
    const nonRefundable = num(inventory?.distribution?.nonRefundable);

    /* License utilization — rendered as per-title % rather than raw stacked
       seat counts. Titles here range from 1 to 300+ seats, so a stacked
       count bar makes the small ones invisible next to the large ones;
       utilization % keeps every title on the same 0-100 scale. */
    const luLabels = licenseUtil.map(l => l.license_softwareName || 'Unknown');
    const luUtilizationPct = licenseUtil.map(l => {
        const total = num(l.license_totalSeats);
        return total > 0 ? Math.round((num(l.license_usedSeats) / total) * 10000) / 100 : 0;
    });

    /* ── Story 107 — Licences cross-filtering ──
       Selection is only possible when the fact table actually came through;
       above the row cap (or on a failed fetch) the tab still renders, from
       the existing Story 105 static distributions, exactly as it did before
       this feature existed. */
    const canCrossFilterLicenses = !!licenseFacts && !licenseFacts.factsTruncated && licenseFacts.facts.length > 0;
    const licenseView = useMemo(
        () => (canCrossFilterLicenses ? aggregateLicenses(licenseFacts!.facts, licenseSelection) : null),
        [canCrossFilterLicenses, licenseFacts, licenseSelection],
    );
    const selectLicense = useCallback((dimension: LicenseDimension, key: string, additive: boolean) => {
        setLicenseSelection((current) => toggleLicenseSelection(current, dimension, key, additive));
    }, []);
    const licenseFilterChips: FilterChip[] = useMemo(() => (
        (Object.entries(licenseSelection) as [LicenseDimension, string[]][])
            .flatMap(([dimension, keys]) => keys.map((key) => ({
                key: `license-${dimension}:${key}`,
                label: `${LICENSE_DIMENSION_LABELS[dimension]}: ${labelForLicenseKey(licenseFacts?.facts || [], dimension, key)}`,
                onRemove: () => setLicenseSelection((current) => toggleLicenseSelection(current, dimension, key, true)),
            })))
    ), [licenseSelection, licenseFacts]);

    /** Unselected slices fade so the selected one reads as the active filter. */
    const licenseSeriesColors = useCallback((rows: LicenseAnalyticsBreakdownRow[], dimension?: LicenseDimension) => {
        const palette = rows.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]);
        if (!dimension || !(licenseSelection[dimension]?.length)) return palette;
        return palette.map((color, i) => (isLicenseSelected(licenseSelection, dimension, rows[i].key) ? color : `${color}33`));
    }, [CHART_COLORS, licenseSelection]);

    /**
     * ApexCharts hands back the clicked index; rows are in the same order.
     * Deferred a tick — see [[apexcharts-click-rerender-crash]]: a synchronous
     * state update inside this callback re-renders the chart while ApexCharts
     * is still mid-interaction with its own SVG, and it throws on a null
     * querySelector. Wired only when cross-filtering is actually possible.
     */
    const licensePointSelection = useCallback((rows: LicenseAnalyticsBreakdownRow[], dimension?: LicenseDimension) => (
        dimension && canCrossFilterLicenses ? {
            dataPointSelection: (event: any, _ctx: any, config: any) => {
                const row = rows[config?.dataPointIndex];
                if (!row) return;
                const additive = !!(event?.ctrlKey || event?.metaKey || event?.shiftKey);
                setTimeout(() => selectLicense(dimension, row.key, additive), 0);
            },
        } : {}
    ), [selectLicense, canCrossFilterLicenses]);

    /** Says a chart is clickable, since nothing else about it suggests that. */
    const licenseSelectableNote = (note: string | undefined, dimension?: LicenseDimension) => {
        if (!dimension || !canCrossFilterLicenses) return note;
        const hint = 'Click to filter · ctrl-click to add';
        return note ? `${note} · ${hint}` : hint;
    };

    const licenseSelectableHBarOptions = (
        rows: LicenseAnalyticsBreakdownRow[], id: string, dimension?: LicenseDimension,
    ): ApexCharts.ApexOptions => ({
        chart: { id, type: 'bar', ...chartFont, toolbar: { show: false }, events: licensePointSelection(rows, dimension) },
        colors: licenseSeriesColors(rows, dimension),
        plotOptions: { bar: { borderRadius: 6, borderRadiusApplication: 'end', horizontal: true, barHeight: '60%', distributed: true } },
        dataLabels: { enabled: false },
        legend: { show: false },
        states: { active: { filter: { type: 'none' } } },
        xaxis: { categories: rows.map(r => r.label), labels: { style: { fontSize: '11px', colors: CHART_INK.axisLabel } } },
        yaxis: { labels: { style: { fontSize: '11px', colors: CHART_INK.axisLabel }, maxWidth: 170 } },
        grid: { borderColor: CHART_INK.gridLine, strokeDashArray: 4, yaxis: { lines: { show: false } } },
        tooltip: { theme: CHART_TOOLTIP_THEME },
    });

    const licenseSelectableBarOptions = (
        rows: LicenseAnalyticsBreakdownRow[], id: string, dimension?: LicenseDimension,
    ): ApexCharts.ApexOptions => ({
        chart: { id, type: 'bar', ...chartFont, toolbar: { show: false }, events: licensePointSelection(rows, dimension) },
        colors: licenseSeriesColors(rows, dimension),
        plotOptions: { bar: { borderRadius: 6, borderRadiusApplication: 'end', columnWidth: '55%' } },
        dataLabels: { enabled: false },
        states: { active: { filter: { type: 'none' } } },
        xaxis: {
            categories: rows.map(r => r.label),
            axisBorder: { show: false }, axisTicks: { show: false },
            labels: { style: { fontSize: '11px', fontWeight: '600', colors: CHART_INK.axisLabel } },
        },
        yaxis: { labels: { style: { fontSize: '11px', colors: CHART_INK.axisLabel } } },
        grid: { borderColor: CHART_INK.gridLine, strokeDashArray: 4, xaxis: { lines: { show: false } } },
        tooltip: { theme: CHART_TOOLTIP_THEME },
    });

    const licenseSelectableDonutOptions = (
        rows: LicenseAnalyticsBreakdownRow[], id: string, dimension?: LicenseDimension,
    ): ApexCharts.ApexOptions => ({
        chart: { id, type: 'donut', ...chartFont, toolbar: { show: false }, events: licensePointSelection(rows, dimension) },
        labels: rows.map(r => r.label),
        colors: licenseSeriesColors(rows, dimension),
        stroke: { show: false },
        dataLabels: { enabled: false },
        states: { active: { filter: { type: 'none' } } },
        plotOptions: { pie: { donut: { size: '68%' }, expandOnClick: false } },
        legend: { position: 'bottom', fontSize: '12px', fontWeight: '600', markers: { size: 6 } },
        tooltip: { theme: CHART_TOOLTIP_THEME },
    });

    /* ── Story 108 — Assets cross-filtering ──
       Selection is only possible when the fact table actually came through;
       above the row cap (or on a failed fetch) the tab still renders, from
       the existing Story 104 static distributions, exactly as it did before
       this feature existed. */
    const canCrossFilterAssets = !!assetFacts && !assetFacts.factsTruncated && assetFacts.facts.length > 0;
    const assetView = useMemo(
        () => (canCrossFilterAssets ? aggregateAssets(assetFacts!.facts, assetSelection) : null),
        [canCrossFilterAssets, assetFacts, assetSelection],
    );
    const selectAsset = useCallback((dimension: AssetDimension, key: string, additive: boolean) => {
        setAssetSelection((current) => toggleAssetSelection(current, dimension, key, additive));
    }, []);
    const assetFilterChips: FilterChip[] = useMemo(() => (
        (Object.entries(assetSelection) as [AssetDimension, string[]][])
            .flatMap(([dimension, keys]) => keys.map((key) => ({
                key: `asset-${dimension}:${key}`,
                label: `${ASSET_DIMENSION_LABELS[dimension]}: ${labelForAssetKey(assetFacts?.facts || [], dimension, key)}`,
                onRemove: () => setAssetSelection((current) => toggleAssetSelection(current, dimension, key, true)),
            })))
    ), [assetSelection, assetFacts]);

    /** Unselected slices fade so the selected one reads as the active filter.
        `basePalette` lets the two fixed-order donuts (warranty status, assigned
        vs unassigned) keep their meaningful colors (red for Expired, muted for
        Unassigned) instead of the generic rotating CHART_COLORS. */
    const assetSeriesColors = useCallback((rows: AssetAnalyticsBreakdownRow[], dimension?: AssetDimension, basePalette?: string[]) => {
        const palette = basePalette ?? rows.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]);
        if (!dimension || !(assetSelection[dimension]?.length)) return palette;
        return palette.map((color, i) => (isAssetSelected(assetSelection, dimension, rows[i].key) ? color : `${color}33`));
    }, [CHART_COLORS, assetSelection]);

    /**
     * ApexCharts hands back the clicked index; rows are in the same order.
     * Deferred a tick — see [[apexcharts-click-rerender-crash]]: a synchronous
     * state update inside this callback re-renders the chart while ApexCharts
     * is still mid-interaction with its own SVG, and it throws on a null
     * querySelector. Wired only when cross-filtering is actually possible.
     */
    const assetPointSelection = useCallback((rows: AssetAnalyticsBreakdownRow[], dimension?: AssetDimension) => (
        dimension && canCrossFilterAssets ? {
            dataPointSelection: (event: any, _ctx: any, config: any) => {
                const row = rows[config?.dataPointIndex];
                if (!row) return;
                const additive = !!(event?.ctrlKey || event?.metaKey || event?.shiftKey);
                setTimeout(() => selectAsset(dimension, row.key, additive), 0);
            },
        } : {}
    ), [selectAsset, canCrossFilterAssets]);

    /** Says a chart is clickable, since nothing else about it suggests that. */
    const assetSelectableNote = (note: string | undefined, dimension?: AssetDimension) => {
        if (!dimension || !canCrossFilterAssets) return note;
        const hint = 'Click to filter · ctrl-click to add';
        return note ? `${note} · ${hint}` : hint;
    };

    const assetSelectableHBarOptions = (
        rows: AssetAnalyticsBreakdownRow[], id: string, dimension?: AssetDimension,
    ): ApexCharts.ApexOptions => ({
        chart: { id, type: 'bar', ...chartFont, toolbar: { show: false }, events: assetPointSelection(rows, dimension) },
        colors: assetSeriesColors(rows, dimension),
        plotOptions: { bar: { borderRadius: 6, borderRadiusApplication: 'end', horizontal: true, barHeight: '60%', distributed: true } },
        dataLabels: { enabled: false },
        legend: { show: false },
        states: { active: { filter: { type: 'none' } } },
        xaxis: { categories: rows.map(r => r.label), labels: { style: { fontSize: '11px', colors: CHART_INK.axisLabel } } },
        yaxis: { labels: { style: { fontSize: '11px', colors: CHART_INK.axisLabel }, maxWidth: 170 } },
        grid: { borderColor: CHART_INK.gridLine, strokeDashArray: 4, yaxis: { lines: { show: false } } },
        tooltip: { theme: CHART_TOOLTIP_THEME },
    });

    const assetSelectableBarOptions = (
        rows: AssetAnalyticsBreakdownRow[], id: string, dimension?: AssetDimension,
    ): ApexCharts.ApexOptions => ({
        chart: { id, type: 'bar', ...chartFont, toolbar: { show: false }, events: assetPointSelection(rows, dimension) },
        colors: assetSeriesColors(rows, dimension),
        plotOptions: { bar: { borderRadius: 6, borderRadiusApplication: 'end', columnWidth: '55%' } },
        dataLabels: { enabled: false },
        states: { active: { filter: { type: 'none' } } },
        xaxis: {
            categories: rows.map(r => r.label),
            axisBorder: { show: false }, axisTicks: { show: false },
            labels: { style: { fontSize: '11px', fontWeight: '600', colors: CHART_INK.axisLabel } },
        },
        yaxis: { labels: { style: { fontSize: '11px', colors: CHART_INK.axisLabel } } },
        grid: { borderColor: CHART_INK.gridLine, strokeDashArray: 4, xaxis: { lines: { show: false } } },
        tooltip: { theme: CHART_TOOLTIP_THEME },
    });

    const assetSelectableDonutOptions = (
        rows: AssetAnalyticsBreakdownRow[], id: string, dimension?: AssetDimension, basePalette?: string[],
    ): ApexCharts.ApexOptions => ({
        chart: { id, type: 'donut', ...chartFont, toolbar: { show: false }, events: assetPointSelection(rows, dimension) },
        labels: rows.map(r => r.label),
        colors: assetSeriesColors(rows, dimension, basePalette),
        stroke: { show: false },
        dataLabels: { enabled: false },
        states: { active: { filter: { type: 'none' } } },
        plotOptions: { pie: { donut: { size: '68%' }, expandOnClick: false } },
        legend: { position: 'bottom', fontSize: '12px', fontWeight: '600', markers: { size: 6 } },
        tooltip: { theme: CHART_TOOLTIP_THEME },
    });

    /* ══════ CHART OPTIONS ══════ */
    const donutOptions: ApexCharts.ApexOptions = {
        chart: { id: 'analytics-asset-status-donut', type: 'donut', ...chartFont, toolbar: { show: false } },
        labels: assetStatusLabels,
        colors: CHART_COLORS,
        stroke: { show: false },
        dataLabels: { enabled: false },
        plotOptions: {
            pie: {
                donut: {
                    size: '70%',
                    labels: {
                        show: true,
                        total: {
                            show: true, label: 'Assets', fontSize: '12px',
                            fontWeight: 700, color: CHART_INK.labelMuted,
                            formatter: () => String(num(global?.totalAssets)),
                        },
                        value: { fontSize: '22px', fontWeight: 800, color: CHART_INK.labelStrong }
                    }
                }
            }
        },
        legend: { position: 'bottom', fontSize: '12px', fontWeight: '600', markers: { size: 6 } },
        tooltip: { theme: CHART_TOOLTIP_THEME }
    };

    const catBarOptions: ApexCharts.ApexOptions = {
        chart: { type: 'bar', ...chartFont, toolbar: { show: false } },
        colors: CHART_COLORS,
        plotOptions: { bar: { borderRadius: 6, borderRadiusApplication: 'end', columnWidth: '55%' } },
        dataLabels: { enabled: false },
        xaxis: {
            categories: catLabels,
            axisBorder: { show: false }, axisTicks: { show: false },
            labels: { style: { fontSize: '11px', fontWeight: '600', colors: CHART_INK.axisLabel } }
        },
        yaxis: { labels: { style: { fontSize: '11px', colors: CHART_INK.axisLabel } } },
        grid: { borderColor: CHART_INK.gridLine, strokeDashArray: 4, xaxis: { lines: { show: false } } },
        tooltip: { theme: CHART_TOOLTIP_THEME }
    };

    /* ══════ Story 104 — distribution chart builders ══════
       Four shapes cover all of the new Assets visuals: a vertical bar for
       short label sets, a horizontal bar for the long-tail ones (people,
       departments, models) whose names never fit under an x-axis, a donut for
       the two-to-four-slice splits, and a line for the monthly trends. */
    const distBarOptions = (id: string, categories: string[]): ApexCharts.ApexOptions => ({
        ...catBarOptions,
        chart: { ...catBarOptions.chart, id },
        xaxis: { ...catBarOptions.xaxis, categories },
    });

    const distHBarOptions = (id: string, categories: string[]): ApexCharts.ApexOptions => ({
        chart: { id, type: 'bar', ...chartFont, toolbar: { show: false } },
        colors: CHART_COLORS,
        plotOptions: { bar: { borderRadius: 6, borderRadiusApplication: 'end', horizontal: true, barHeight: '60%', distributed: true } },
        dataLabels: { enabled: false },
        legend: { show: false }, // distributed bars would otherwise legend every category
        xaxis: { categories, labels: { style: { fontSize: '11px', colors: CHART_INK.axisLabel } } },
        yaxis: { labels: { style: { fontSize: '11px', colors: CHART_INK.axisLabel }, maxWidth: 170 } },
        grid: { borderColor: CHART_INK.gridLine, strokeDashArray: 4, yaxis: { lines: { show: false } } },
        tooltip: { theme: CHART_TOOLTIP_THEME },
    });

    const distDonutOptions = (id: string, labels: string[], colors: string[] = CHART_COLORS): ApexCharts.ApexOptions => ({
        chart: { id, type: 'donut', ...chartFont, toolbar: { show: false } },
        labels,
        colors,
        stroke: { show: false },
        dataLabels: { enabled: false },
        plotOptions: { pie: { donut: { size: '68%' } } },
        legend: { position: 'bottom', fontSize: '12px', fontWeight: '600', markers: { size: 6 } },
        tooltip: { theme: CHART_TOOLTIP_THEME },
    });

    const distTrendOptions = (id: string, categories: string[], colors?: string[]): ApexCharts.ApexOptions => ({
        chart: { id, type: 'line', ...chartFont, toolbar: { show: false }, zoom: { enabled: false } },
        colors: colors ?? [CHART_COLORS[0]],
        stroke: { curve: 'smooth', width: 3 },
        markers: { size: 4 },
        dataLabels: { enabled: false },
        xaxis: {
            categories,
            axisBorder: { show: false }, axisTicks: { show: false },
            labels: { style: { fontSize: '11px', fontWeight: '600', colors: CHART_INK.axisLabel } },
        },
        yaxis: { labels: { style: { fontSize: '11px', colors: CHART_INK.axisLabel }, formatter: (v: number) => String(Math.round(v)) } },
        grid: { borderColor: CHART_INK.gridLine, strokeDashArray: 4, xaxis: { lines: { show: false } } },
        legend: { position: 'top', horizontalAlign: 'right', fontSize: '11px', fontWeight: '600' },
        tooltip: { theme: CHART_TOOLTIP_THEME },
    });

    const luBarOptions: ApexCharts.ApexOptions = {
        chart: { id: 'analytics-license-utilization', type: 'bar', ...chartFont, toolbar: { show: false } },
        colors: CHART_COLORS,
        plotOptions: {
            bar: {
                borderRadius: 6, borderRadiusApplication: 'end', horizontal: true, barHeight: '55%',
                distributed: true,
            },
        },
        dataLabels: {
            enabled: true, offsetX: 6, style: { fontSize: '11px', fontWeight: '700', colors: [CHART_INK.labelStrong] },
            formatter: (v: number) => `${v}%`,
        },
        legend: { show: false }, // distributed bars would otherwise legend every title
        xaxis: {
            categories: luLabels, max: 100,
            labels: { style: { fontSize: '11px', colors: CHART_INK.axisLabel }, formatter: (v: string) => `${v}%` },
        },
        yaxis: { labels: { style: { fontSize: '11px', colors: '#374151' }, maxWidth: 160 } },
        grid: { borderColor: CHART_INK.gridLine, strokeDashArray: 4, yaxis: { lines: { show: false } } },
        tooltip: { theme: CHART_TOOLTIP_THEME, y: { formatter: (v: number) => `${v}% utilized` } },
    };

    const inventoryDonutOptions: ApexCharts.ApexOptions = {
        chart: { id: 'analytics-inventory-refundable-donut', type: 'donut', ...chartFont, toolbar: { show: false } },
        labels: ['Refundable', 'Non-Refundable'],
        colors: ['#4f46e5', '#d97706'],
        stroke: { show: false },
        dataLabels: { enabled: false },
        plotOptions: {
            pie: {
                donut: {
                    size: '65%',
                    labels: {
                        show: true,
                        total: {
                            show: true, label: 'Stock Items', fontSize: '12px',
                            fontWeight: 700, color: CHART_INK.labelMuted,
                            formatter: () => String(refundable + nonRefundable),
                        },
                        value: { fontSize: '22px', fontWeight: 800, color: CHART_INK.labelStrong }
                    }
                }
            }
        },
        legend: { position: 'bottom', fontSize: '12px', fontWeight: '600', markers: { size: 6 } },
        tooltip: { theme: CHART_TOOLTIP_THEME }
    };

    /* ══════ LOADING ══════ */
    if (loading) {
        return (
            <div className="analytics-loading text-muted-foreground">
                <RefreshCw className="animate-spin" size={28} />
                <span>Loading analytics…</span>
            </div>
        );
    }

    /* ══════ NO ACCESS ══════ */
    if (!hasAnyAnalyticsTab) {
        return (
            <div className="analytics-loading text-muted-foreground">
                <ShieldAlert size={28} />
                <span>You do not have permission to view any Analytics tab. Contact your administrator if you believe this is incorrect.</span>
            </div>
        );
    }

    /* ══════ TABS ══════ */
    const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = ([
        { key: 'executive', label: 'Executive', icon: <LayoutGrid size={15} /> },
        { key: 'assets', label: 'Assets', icon: <Server size={15} /> },
        { key: 'licenses', label: 'Licenses', icon: <Key size={15} /> },
        { key: 'inventory', label: 'Inventory', icon: <Package size={15} /> },
        { key: 'cloud', label: 'Cloud', icon: <Cloud size={15} /> },
    ] as const).filter((t) => canView[t.key]);

    /* ══════ PANELS ══════ */

    const renderExecutive = () => {
        // KPI #7 (IT Procurement Spend) is a best-effort partial figure: inventory
        // purchase spend + monthly-equivalent license spend. Asset acquisition cost
        // has no monthly-total endpoint yet, so it is explicitly excluded rather than
        // silently under-counted without a label — see plan §9 for the full KPI list.
        const procurementSpend = num(inventory?.thisMonthSpend) + (num(licenses?.annualizedSpend) / 12);

        const riskRows: (RiskRowProps & { key: string })[] = alerts ? [
            { key: 'warrantyExpired', label: 'Warranty Expired', value: alerts.warrantyExpired, severity: 'critical', kpiId: 'kpi-warranty-status', onClick: setActiveKpiId },
            { key: 'warrantyExpiring30', label: 'Warranty Expiring (30d)', value: alerts.warrantyExpiring30, severity: 'warning', kpiId: 'kpi-warranty-status', onClick: setActiveKpiId },
            { key: 'expiredLicenses', label: 'Expired Licenses', value: alerts.expiredLicenses, severity: 'critical', kpiId: 'kpi-license-expiry', onClick: setActiveKpiId },
            { key: 'licensesExpiring30', label: 'Licenses Expiring (30d)', value: alerts.licensesExpiring30, severity: 'warning', kpiId: 'kpi-license-expiry', onClick: setActiveKpiId },
            { key: 'overAllocatedLicenses', label: 'Over-Allocated Licenses', value: alerts.overAllocatedLicenses, severity: 'critical', kpiId: 'kpi-license-seats', onClick: setActiveKpiId },
            { key: 'outOfStock', label: 'Out-of-Stock Items', value: alerts.outOfStock, severity: 'critical', kpiId: 'kpi-low-stock', onClick: setActiveKpiId },
            { key: 'lowStockItems', label: 'Low-Stock Items', value: alerts.lowStockItems, severity: 'warning', kpiId: 'kpi-low-stock', onClick: setActiveKpiId },
            { key: 'overdueReturns', label: 'Overdue Returns', value: alerts.overdueReturns, severity: 'warning', kpiId: 'kpi-overdue', onClick: setActiveKpiId },
            { key: 'damagedReturns', label: 'Damaged Returns', value: alerts.damagedReturns, severity: 'info', kpiId: 'kpi-damaged-returns', onClick: setActiveKpiId },
            { key: 'assetsUnassigned', label: 'Unassigned Assets', value: alerts.assetsUnassigned, severity: 'info', kpiId: 'kpi-unassigned-assets', onClick: setActiveKpiId },
        ] : [];
        const visibleRiskRows = riskRows.filter(r => r.value > 0);

        const portfolioLabels = ['Assets', 'Licenses', 'Inventory Items'];
        const portfolioSeries = [num(global?.totalAssets), num(global?.totalLicenses), num(global?.totalInventoryItems)];
        const portfolioOptions: ApexCharts.ApexOptions = {
            chart: { id: 'analytics-portfolio-composition', type: 'bar', ...chartFont, toolbar: { show: false } },
            colors: CHART_COLORS,
            plotOptions: { bar: { borderRadius: 6, borderRadiusApplication: 'end', horizontal: true, barHeight: '50%', distributed: true } },
            dataLabels: { enabled: true, style: { fontSize: '12px', fontWeight: 700, colors: [CHART_INK.labelStrong] }, offsetX: 20 },
            xaxis: { categories: portfolioLabels, labels: { style: { fontSize: '11px', colors: CHART_INK.axisLabel } } },
            yaxis: { labels: { style: { fontSize: '12px', fontWeight: '600', colors: '#374151' } } },
            grid: { borderColor: CHART_INK.gridLine, strokeDashArray: 4, yaxis: { lines: { show: false } } },
            legend: { show: false },
            tooltip: { theme: CHART_TOOLTIP_THEME },
        };

        return (
            <div className="analytics-panel">
                {execPartialError && (
                    <div className="analytics-partial-banner">
                        <Info size={14} />
                        <span>{execPartialError}</span>
                    </div>
                )}

                <div className={KPI_GRID}>
                    <KpiCard
                        icon={<Server size={18} />} label="Total Active Assets" value={num(global?.totalAssets)}
                        accent="#4f46e5" iconBg="rgba(79,70,229,0.10)" kpiId="kpi-total-assets" onClick={setActiveKpiId}
                        sub={undefined}
                    />
                    <KpiCard
                        icon={<Gauge size={18} />} label="Asset Utilization Rate" value={formatPercent(num(assets?.utilizationPercentage))}
                        accent="#059669" iconBg="rgba(13,148,136,0.12)" kpiId="kpi-utilization" onClick={setActiveKpiId}
                    />
                    <KpiCard
                        icon={<Activity size={18} />} label="Current Asset Book Value"
                        value={canViewFinancial ? formatDisplayAmount(num(assetFinancial?.totalBookValue)) : 'Restricted'}
                        sub={canViewFinancial
                            ? (assetFinancial?.missingRateCurrencies?.length
                                ? `Excludes currencies with no rate: ${assetFinancial.missingRateCurrencies.join(', ')}`
                                : undefined)
                            : 'Requires financial access permission'}
                        accent="#0891b2" iconBg="#ecfeff"
                        kpiId={canViewFinancial ? 'kpi-book-value' : undefined} onClick={canViewFinancial ? setActiveKpiId : undefined}
                    />
                    <KpiCard
                        icon={<DollarSign size={18} />} label="Annualized License Spend"
                        value={canViewFinancial ? formatDisplayAmount(num(licenses?.annualizedSpend)) : 'Restricted'}
                        sub={canViewFinancial
                            ? (licenses?.missingRateCurrencies?.length
                                ? `Excludes currencies with no rate: ${licenses.missingRateCurrencies.join(', ')}`
                                : undefined)
                            : 'Requires financial access permission'}
                        accent="#7c3aed" iconBg="rgba(124,58,237,0.10)"
                        kpiId={canViewFinancial ? 'kpi-annual-license-spend' : undefined} onClick={canViewFinancial ? setActiveKpiId : undefined}
                    />
                    <KpiCard
                        icon={<Key size={18} />} label="License Seat Utilization" value={formatPercent(num(licenses?.utilizationPct))}
                        sub={`${fmt(num(licenses?.usedSeats))} / ${fmt(num(licenses?.totalSeats))} seats used`}
                        accent="#d97706" iconBg="rgba(217,119,6,0.10)" kpiId="kpi-license-seats" onClick={setActiveKpiId}
                    />
                    <KpiCard
                        icon={<ShieldAlert size={18} />} label="Open Operational Risk Count" value={num(alerts?.totalCritical)}
                        sub="Warranty, license, stock & return exceptions" accent="#e11d48" iconBg="rgba(225,29,72,0.10)"
                    />
                    <KpiCard
                        icon={<Package size={18} />} label="Estimated Inventory Stock Value" value="Pending costing method"
                        sub="Awaiting Finance sign-off on valuation basis (see plan §9)" accent="#94a3b8" iconBg="#f8fafc"
                    />
                    <KpiCard
                        icon={<Layers size={18} />} label="IT Procurement Spend (this month, partial)"
                        value={canViewFinancial ? formatDisplayAmount(procurementSpend) : 'Restricted'}
                        sub={canViewFinancial ? 'Inventory + license only — asset acquisition spend not yet included' : 'Requires financial access permission'}
                        accent="#0891b2" iconBg="#ecfeff"
                    />
                </div>

                {/* Week-over-week trend strip — powered by the analytics_daily_snapshot table.
                    Shows "Comparison unavailable" per domain until 7+ days of ACTUAL snapshots exist. */}
                <Card>
                    <CardContent className="p-5">
                        <SectionHead title="Week-over-Week Trend" note="vs. 7 days ago · from daily snapshots" />
                        <div className="analytics-trend-strip grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                            <div>
                                <p className="analytics-trend-strip-label">Total Active Assets</p>
                                <TrendNote comparison={trends.asset} metricKey="totalActive" />
                            </div>
                            <div>
                                <p className="analytics-trend-strip-label">Asset Book Value</p>
                                <TrendNote comparison={trends.asset} metricKey="currentValueBase" />
                            </div>
                            <div>
                                <p className="analytics-trend-strip-label">License Seats Used</p>
                                <TrendNote comparison={trends.license} metricKey="usedSeats" />
                            </div>
                            <div>
                                <p className="analytics-trend-strip-label">Annualized License Cost</p>
                                <TrendNote comparison={trends.license} metricKey="annualizedCostBase" />
                            </div>
                            <div>
                                <p className="analytics-trend-strip-label">Available Stock Units</p>
                                <TrendNote comparison={trends.inventory} metricKey="availableStockUnits" />
                            </div>
                        </div>
                        {canManageSnapshots && snapshotHealth && <SnapshotHealthStrip health={snapshotHealth} />}
                    </CardContent>
                </Card>

                {/* Action-oriented risk queue */}
                <FocusableVisual title="Prioritized Action Queue" note={alerts ? `${visibleRiskRows.length} open exception${visibleRiskRows.length === 1 ? '' : 's'}` : undefined} flush>
                    {() => (
                        execLoading && !alerts ? (
                            <EmptyStateCard title="Loading risk queue…" spinning />
                        ) : !alerts ? (
                            <EmptyStateCard title="Risk queue unavailable" reason="Alert data could not be loaded for this period." />
                        ) : visibleRiskRows.length === 0 ? (
                            <EmptyStateCard icon={<CheckCircle size={20} />} title="No open exceptions — everything is within threshold." />
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Exception</TableHead>
                                        <TableHead>Count</TableHead>
                                        <TableHead>Action</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {visibleRiskRows.map(({ key, ...r }) => <RiskRow key={key} {...r} />)}
                                </TableBody>
                            </Table>
                        )
                    )}
                </FocusableVisual>

                {/* Portfolio composition — how the managed estate splits across the three domains */}
                <FocusableVisual title="Portfolio Composition" note="Assets · Licenses · Inventory items" height={220}>
                    {({ height }) => (
                        portfolioSeries.some(v => v > 0)
                            ? <Chart options={portfolioOptions} series={[{ name: 'Count', data: portfolioSeries }]} type="bar" height={height} />
                            : <EmptyStateCard title="No portfolio data" reason="Nothing has been recorded across assets, licenses, or inventory yet." />
                    )}
                </FocusableVisual>
            </div>
        );
    };

    /* ── Story 108 — effective chart data for the cross-filterable Assets
       charts: the live fact-table view when cross-filtering is possible,
       falling back to Story 104's static distributions (unaffected by
       selection) otherwise — the same tab that rendered before this feature
       existed, minus the "click to filter" affordance. ── */
    const assetByDepartmentRows: AssetAnalyticsBreakdownRow[] = canCrossFilterAssets
        ? assetView!.byDepartment
        : (assetDistributions?.byDepartment || []).map(d => ({ key: d.label, label: d.label, count: d.count }));
    const assetByManufacturerRows: AssetAnalyticsBreakdownRow[] = canCrossFilterAssets
        ? assetView!.byManufacturer
        : (assetDistributions?.byManufacturer || []).map(d => ({ key: d.label, label: d.label, count: d.count }));
    const assetByVendorRows: AssetAnalyticsBreakdownRow[] = canCrossFilterAssets
        ? assetView!.byVendor
        : (assetDistributions?.byVendor || []).map(d => ({ key: d.label, label: d.label, count: d.count }));
    const assetByModelRows: AssetAnalyticsBreakdownRow[] = canCrossFilterAssets
        ? assetView!.byModel
        : (assetDistributions?.byModel || []).map(d => ({ key: d.label, label: d.label, count: d.count }));
    const assetByLifecycleStageRows: AssetAnalyticsBreakdownRow[] = canCrossFilterAssets
        ? assetView!.byLifecycleStage
        : (assetDistributions?.byLifecycleStage || []).map(d => ({ key: d.label, label: d.label, count: d.count }));
    const assetByWarrantyStatusRows: AssetAnalyticsBreakdownRow[] = canCrossFilterAssets
        ? assetView!.byWarrantyStatus
        : (assetDistributions?.byWarrantyStatus || []).map(d => ({ key: d.label, label: d.label, count: d.count }));
    const assetByAgeRows: AssetAnalyticsBreakdownRow[] = canCrossFilterAssets
        ? assetView!.byAge
        : (assetDistributions?.byAge || []).map(d => ({ key: d.label, label: d.label, count: d.count }));
    const assetAssignedVsUnassignedRows: AssetAnalyticsBreakdownRow[] = canCrossFilterAssets
        ? assetView!.assignedVsUnassigned
        : (assetDistributions?.assignedVsUnassigned || []).map(d => ({ key: d.label, label: d.label, count: d.count }));

    const renderAssets = () => (
        <div className="analytics-panel">
            {/* Story 108 — cross-filtering. Selection lives on this tab only
                (not the page's own AnalyticsAdvancedFilters), so its chips get
                their own bar, matching how the Licences tab surfaces its
                selection. Placed above the KPI strip so an active selection is
                the first thing read on the tab, not something scrolled past. */}
            {assetFactsLoading && !canCrossFilterAssets && (
                <p className="text-xs text-muted-foreground">Enabling click-to-filter on the charts below…</p>
            )}
            {canCrossFilterAssets && assetFilterChips.length > 0 && (
                <div className="rounded-md border bg-card">
                    <div className="flex items-center justify-end gap-3 px-4 pt-3">
                        <span className="text-xs text-muted-foreground">
                            {fmt(assetView!.scopedCount)} of {fmt(assetFacts!.facts.length)} assets
                        </span>
                    </div>
                    <FilterChipsBar chips={assetFilterChips} onClearAll={() => setAssetSelection({})} />
                </div>
            )}
            <div className={KPI_GRID}>
                <KpiCard icon={<Server size={18} />} label="Total Assets" value={num(global?.totalAssets)} accent="#4f46e5" iconBg="rgba(79,70,229,0.10)" kpiId="kpi-total-assets" onClick={setActiveKpiId} />
                <KpiCard icon={<TrendingUp size={18} />} label="Utilization" value={formatPercent(num(assets?.utilizationPercentage))} sub="Deployed / Total" accent="#059669" iconBg="rgba(13,148,136,0.12)" kpiId="kpi-utilization" onClick={setActiveKpiId} />
                <KpiCard icon={<Cpu size={18} />} label="Recently Added" value={num(assets?.recentlyAdded)} sub="Last 30 days" accent="#7c3aed" iconBg="rgba(124,58,237,0.10)" kpiId="kpi-new-assets" onClick={setActiveKpiId} />
                <KpiCard icon={<ShieldAlert size={18} />} label="Warranty Expiring" value={num(assets?.warrantyExpiring60)} sub="Within 60 days" accent="#e11d48" iconBg="rgba(225,29,72,0.10)" kpiId="kpi-warranty-status" onClick={() => openWarrantyDrilldown('60')} />
                <KpiCard icon={<AlertTriangle size={18} />} label="Expiring in 30d" value={num(assets?.warrantyExpiring30)} sub="Critical window" accent="#d97706" iconBg="rgba(217,119,6,0.10)" kpiId="kpi-warranty-status" onClick={() => openWarrantyDrilldown('30')} />
                <KpiCard icon={<XCircle size={18} />} label="Asset Expired" value={num(assets?.warrantyExpired)} sub="Warranty already expired" accent="#e11d48" iconBg="rgba(225,29,72,0.10)" kpiId="kpi-warranty-status" onClick={() => openWarrantyDrilldown('expired')} />
                <KpiCard
                    icon={<Activity size={18} />} label="Total Asset Purchase Cost"
                    value={canViewFinancial ? formatDisplayAmount(num(global?.totalAssetPurchaseCost)) : 'Restricted'}
                    sub={canViewFinancial
                        ? (global?.missingRateCurrencies?.length
                            ? `Excludes currencies with no rate: ${global.missingRateCurrencies.join(', ')}`
                            : 'Sum of purchase cost — not current (depreciated) book value')
                        : 'Requires financial access permission'}
                    accent="#0891b2" iconBg="#ecfeff"
                />
                <KpiCard
                    icon={<DollarSign size={18} />} label="Total Rental Cost"
                    value={canViewFinancial ? formatDisplayAmount(num(global?.totalRentalCostAllTime)) : 'Restricted'}
                    sub={canViewFinancial
                        ? (global?.rentalCostMissingRateCurrencies?.length
                            ? `Excludes currencies with no rate: ${global.rentalCostMissingRateCurrencies.join(', ')}`
                            : 'All time — rental start to dispose/today')
                        : 'Requires financial access permission'}
                    accent="#7c3aed" iconBg="rgba(124,58,237,0.10)"
                    kpiId={canViewFinancial ? 'kpi-rental-cost-total' : undefined}
                    onClick={canViewFinancial ? setActiveKpiId : undefined}
                />
            </div>
            <div className={CONTENT_GRID}>
                <FocusableVisual className={COL_PRIMARY} title="Assets by Category">
                    {({ height }) => (
                        catSeries.length > 0
                            ? <Chart options={{ ...catBarOptions, chart: { ...catBarOptions.chart, id: 'analytics-assets-by-category' } }} series={[{ name: 'Assets', data: catSeries }]} type="bar" height={height} />
                            : <EmptyStateCard title="No category data" reason="Assets haven't been tagged with a category yet." />
                    )}
                </FocusableVisual>
                <FocusableVisual className={COL_SECONDARY} title="Assets by Status">
                    {({ height }) => (
                        assetStatusSeries.some(v => v > 0)
                            ? <Chart options={donutOptions} series={assetStatusSeries} type="donut" height={height} />
                            : <EmptyStateCard title="No status data" />
                    )}
                </FocusableVisual>
            </div>
            {/* Status breakdown table */}
            <FocusableVisual title="Status Breakdown" flush>
                {() => (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Status</TableHead>
                                <TableHead>Count</TableHead>
                                <TableHead>Share</TableHead>
                                <TableHead>Distribution</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(assets?.byStatus || []).map((s, i) => {
                                const count = parseInt(s.count);
                                const share = pct(count, num(global?.totalAssets));
                                return (
                                    <TableRow key={s.status}>
                                        <TableCell>
                                            <span className="status-dot" style={({ background: CHART_COLORS[i % CHART_COLORS.length] }) as React.CSSProperties} />
                                            {s.status.charAt(0).toUpperCase() + s.status.slice(1)}
                                        </TableCell>
                                        <TableCell className="tabular-nums font-semibold text-foreground">{fmt(count)}</TableCell>
                                        <TableCell className="tabular-nums text-muted-foreground">{formatPercent(share)}</TableCell>
                                        <TableCell className="w-40">
                                            <div className="mini-bar-track">
                                                <div className="mini-bar-fill" style={({ width: `${share}%`, background: CHART_COLORS[i % CHART_COLORS.length] }) as React.CSSProperties} />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </FocusableVisual>

            {/* Phase 3 — Asset lifecycle KPIs (#14, #20, #24, #26). Live-only —
                no snapshot trend exists for these yet. */}
            <Card>
                <CardContent className="p-5 pb-0">
                    <SectionHead title="Lifecycle & Data Quality" note="Live data — no historical trend yet" />
                </CardContent>
                <CardContent className={cn('p-5 pt-0', (assetLifecycleLoading || !assetLifecycle) && 'p-0')}>
                    {assetLifecycleLoading && !assetLifecycle ? (
                        <EmptyStateCard title="Loading lifecycle KPIs…" spinning />
                    ) : !assetLifecycle ? (
                        <EmptyStateCard title="Lifecycle KPIs unavailable" reason="This data could not be loaded for the current filters." />
                    ) : (
                        <div className={KPI_GRID}>
                            <KpiCard icon={<Clock size={18} />} label="Near Useful Life" value={num(assetLifecycle.nearUsefulLife)} sub="Within 90 days of end-of-life" accent="#d97706" iconBg="rgba(217,119,6,0.10)" kpiId="kpi-useful-life" onClick={() => openUsefulLifeDrilldown('near')} />
                            <KpiCard icon={<XCircle size={18} />} label="Beyond Useful Life" value={num(assetLifecycle.beyondUsefulLife)} sub="Replacement candidates" accent="#e11d48" iconBg="rgba(225,29,72,0.10)" kpiId="kpi-useful-life" onClick={() => openUsefulLifeDrilldown('beyond')} />
                            <KpiCard
                                icon={<TrendingDown size={18} />} label="Disposal Rate (this month)"
                                value={formatAvailability(assetLifecycle.disposalRatePct, (v) => formatPercent(v)).display}
                                sub={formatAvailability(assetLifecycle.disposalRatePct, (v) => formatPercent(v)).unavailable ? assetLifecycle.disposalRatePct.reason : `${fmt(assetLifecycle.disposedThisMonth)} disposed`}
                                accent="#0891b2" iconBg="#ecfeff"
                            />
                            <KpiCard
                                icon={<Percent size={18} />} label="Data Completeness Score"
                                value={formatAvailability(assetLifecycle.dataCompletenessScore, (v) => formatPercent(v)).display}
                                sub={formatAvailability(assetLifecycle.dataCompletenessScore, (v) => formatPercent(v)).unavailable ? assetLifecycle.dataCompletenessScore.reason : 'Serial, model, dates, cost, location'}
                                accent="#7c3aed" iconBg="rgba(124,58,237,0.10)"
                            />
                        </div>
                    )}
                </CardContent>
            </Card>
            {assetLifecycle && (
                <div className={CONTENT_GRID}>
                    <FocusableVisual className={COL_PRIMARY} title="Assets by Location">
                        {({ height }) => (
                            assetLifecycle.byLocation.length > 0
                                ? <Chart
                                    options={{ ...catBarOptions, chart: { ...catBarOptions.chart, id: 'analytics-assets-by-location' }, xaxis: { ...catBarOptions.xaxis, categories: assetLifecycle.byLocation.map(l => l.site) } }}
                                    series={[{ name: 'Assets', data: assetLifecycle.byLocation.map(l => parseInt(l.count)) }]}
                                    type="bar" height={height}
                                />
                                : <EmptyStateCard title="No location data" reason="No site/location has been recorded on these assets." />
                        )}
                    </FocusableVisual>
                    <FocusableVisual className={COL_SECONDARY} title="Data Completeness — Missing Field Counts">
                        {({ focused }) => (
                            assetLifecycle.missingFieldBreakdown.length > 0 ? (
                                <div className={cn(!focused && 'max-h-[300px] overflow-y-auto')}>
                                    <Table>
                                        <TableHeader><TableRow><TableHead>Field</TableHead><TableHead>Missing</TableHead></TableRow></TableHeader>
                                        <TableBody>
                                            {assetLifecycle.missingFieldBreakdown.map((f) => (
                                                <TableRow key={f.field}>
                                                    <TableCell>{f.field}</TableCell>
                                                    <TableCell className="tabular-nums font-semibold text-foreground">{fmt(f.missingCount)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            ) : <EmptyStateCard title="No missing-field data" />
                        )}
                    </FocusableVisual>
                </div>
            )}

            {/* Story 104 — portfolio breakdowns. Each chart is independently
                empty-stated: a department with no assigned assets is a real
                answer, not a failure, and must not read as a broken chart. */}
            <Card>
                <CardContent className="p-5 pb-0">
                    <SectionHead title="Portfolio Breakdown" note={assetDistributions ? `Long-tail charts show the top ${assetDistributions.topN}` : 'Live data'} />
                </CardContent>
                <CardContent className={cn('p-5 pt-0', (assetDistributionsLoading || !assetDistributions) && 'p-0')}>
                    {assetDistributionsLoading && !assetDistributions ? (
                        <EmptyStateCard title="Loading breakdowns…" spinning />
                    ) : !assetDistributions ? (
                        <EmptyStateCard title="Breakdowns unavailable" reason="This data could not be loaded for the current filters." />
                    ) : null}
                </CardContent>
            </Card>

            {assetDistributions && (<>
                <div className={CONTENT_GRID}>
                    <FocusableVisual className={COL_PRIMARY} title="Assets by Department" note={assetSelectableNote('Derived from the assigned user', 'department')}>
                        {({ height }) => (
                            assetByDepartmentRows.length > 0
                                ? <Chart
                                    key={`department-${canCrossFilterAssets}`}
                                    options={canCrossFilterAssets
                                        ? assetSelectableHBarOptions(assetByDepartmentRows, 'analytics-assets-by-department', 'department')
                                        : distHBarOptions('analytics-assets-by-department', assetByDepartmentRows.map(d => d.label))}
                                    series={[{ name: 'Assets', data: assetByDepartmentRows.map(d => d.count) }]}
                                    type="bar" height={Math.max(height, assetByDepartmentRows.length * 34)}
                                />
                                : <EmptyStateCard title="No department data" reason="No assets are assigned to a user with a department." />
                        )}
                    </FocusableVisual>
                    <FocusableVisual className={COL_SECONDARY} title="Assigned vs Unassigned" note={assetSelectableNote(undefined, 'assignedStatus')}>
                        {({ height }) => (
                            assetAssignedVsUnassignedRows.length > 0
                                ? <Chart
                                    key={`assigned-status-${canCrossFilterAssets}`}
                                    options={canCrossFilterAssets
                                        ? assetSelectableDonutOptions(assetAssignedVsUnassignedRows, 'analytics-assigned-vs-unassigned', 'assignedStatus', ['#4f46e5', CHART_INK.trackMuted])
                                        : distDonutOptions('analytics-assigned-vs-unassigned', assetAssignedVsUnassignedRows.map(d => d.label), ['#4f46e5', CHART_INK.trackMuted])}
                                    series={assetAssignedVsUnassignedRows.map(d => d.count)}
                                    type="donut" height={height}
                                />
                                : <EmptyStateCard title="No assets in scope" />
                        )}
                    </FocusableVisual>
                </div>

                <div className={CONTENT_GRID}>
                    <FocusableVisual className={COL_PRIMARY} title="Assets by Assigned User" note={`Top ${assetDistributions.topN} holders`}>
                        {({ height, focused }) => (
                            assetDistributions.byAssignedUser.length > 0
                                ? <div className={cn(!focused && 'max-h-[420px] overflow-y-auto')}>
                                    <Chart
                                        options={distHBarOptions('analytics-assets-by-user', assetDistributions.byAssignedUser.map(d => d.label))}
                                        series={[{ name: 'Assets', data: assetDistributions.byAssignedUser.map(d => d.count) }]}
                                        type="bar" height={Math.max(height, assetDistributions.byAssignedUser.length * 34)}
                                    />
                                </div>
                                : <EmptyStateCard title="No assignment data" reason="No assets are currently assigned to a user." />
                        )}
                    </FocusableVisual>
                    <FocusableVisual className={COL_SECONDARY} title="Assets by Business Owner" note={`Top ${assetDistributions.topN}`}>
                        {({ height, focused }) => (
                            assetDistributions.byBusinessOwner.length > 0
                                ? <div className={cn(!focused && 'max-h-[420px] overflow-y-auto')}>
                                    <Chart
                                        options={distHBarOptions('analytics-assets-by-owner', assetDistributions.byBusinessOwner.map(d => d.label))}
                                        series={[{ name: 'Assets', data: assetDistributions.byBusinessOwner.map(d => d.count) }]}
                                        type="bar" height={Math.max(height, assetDistributions.byBusinessOwner.length * 34)}
                                    />
                                </div>
                                : <EmptyStateCard title="No business owner data" reason="No assets have a business owner recorded." />
                        )}
                    </FocusableVisual>
                </div>

                <div className={CONTENT_GRID}>
                    <FocusableVisual className={COL_PRIMARY} title="Assets by Manufacturer" note={assetSelectableNote(`Top ${assetDistributions.topN}`, 'manufacturer')}>
                        {({ height }) => (
                            assetByManufacturerRows.length > 0
                                ? <Chart
                                    key={`manufacturer-${canCrossFilterAssets}`}
                                    options={canCrossFilterAssets
                                        ? assetSelectableBarOptions(assetByManufacturerRows, 'analytics-assets-by-manufacturer', 'manufacturer')
                                        : distBarOptions('analytics-assets-by-manufacturer', assetByManufacturerRows.map(d => d.label))}
                                    series={[{ name: 'Assets', data: assetByManufacturerRows.map(d => d.count) }]}
                                    type="bar" height={height}
                                />
                                : <EmptyStateCard title="No manufacturer data" reason="No brand has been recorded on these assets." />
                        )}
                    </FocusableVisual>
                    <FocusableVisual className={COL_SECONDARY} title="Assets by Age" note={assetSelectableNote('From purchase or rental-receipt date', 'ageBucket')}>
                        {({ height }) => (
                            assetByAgeRows.length > 0
                                ? <Chart
                                    key={`age-${canCrossFilterAssets}`}
                                    options={canCrossFilterAssets
                                        ? assetSelectableBarOptions(assetByAgeRows, 'analytics-assets-by-age', 'ageBucket')
                                        : distBarOptions('analytics-assets-by-age', assetByAgeRows.map(d => d.label))}
                                    series={[{ name: 'Assets', data: assetByAgeRows.map(d => d.count) }]}
                                    type="bar" height={height}
                                />
                                : <EmptyStateCard title="No age data" reason="No acquisition dates have been recorded." />
                        )}
                    </FocusableVisual>
                </div>

                <div className={CONTENT_GRID}>
                    <FocusableVisual className={COL_PRIMARY} title="Assets by Vendor" note={assetSelectableNote(`Top ${assetDistributions.topN}`, 'vendor')}>
                        {({ height }) => (
                            assetByVendorRows.length > 0
                                ? <Chart
                                    key={`vendor-${canCrossFilterAssets}`}
                                    options={canCrossFilterAssets
                                        ? assetSelectableBarOptions(assetByVendorRows, 'analytics-assets-by-vendor', 'vendor')
                                        : distBarOptions('analytics-assets-by-vendor', assetByVendorRows.map(d => d.label))}
                                    series={[{ name: 'Assets', data: assetByVendorRows.map(d => d.count) }]}
                                    type="bar" height={height}
                                />
                                : <EmptyStateCard title="No vendor data" reason="No vendor has been recorded on these assets." />
                        )}
                    </FocusableVisual>
                    <FocusableVisual className={COL_SECONDARY} title="Assets by Model" note={assetSelectableNote(`Top ${assetDistributions.topN}`, 'model')}>
                        {({ height, focused }) => (
                            assetByModelRows.length > 0
                                ? <div className={cn(!focused && 'max-h-[420px] overflow-y-auto')}>
                                    <Chart
                                        key={`model-${canCrossFilterAssets}`}
                                        options={canCrossFilterAssets
                                            ? assetSelectableHBarOptions(assetByModelRows, 'analytics-assets-by-model', 'model')
                                            : distHBarOptions('analytics-assets-by-model', assetByModelRows.map(d => d.label))}
                                        series={[{ name: 'Assets', data: assetByModelRows.map(d => d.count) }]}
                                        type="bar" height={Math.max(height, assetByModelRows.length * 34)}
                                    />
                                </div>
                                : <EmptyStateCard title="No model data" reason="No model has been recorded on these assets." />
                        )}
                    </FocusableVisual>
                </div>

                <div className={CONTENT_GRID}>
                    <FocusableVisual className={COL_PRIMARY} title="Asset Lifecycle Status" note={assetSelectableNote('Procured → Deployed → Maintained → Retired', 'lifecycleStage')}>
                        {({ height }) => (
                            assetByLifecycleStageRows.some(s => s.count > 0)
                                ? <Chart
                                    key={`lifecycle-${canCrossFilterAssets}`}
                                    options={canCrossFilterAssets
                                        ? assetSelectableBarOptions(assetByLifecycleStageRows, 'analytics-asset-lifecycle-stage', 'lifecycleStage')
                                        : distBarOptions('analytics-asset-lifecycle-stage', assetByLifecycleStageRows.map(d => d.label))}
                                    series={[{ name: 'Assets', data: assetByLifecycleStageRows.map(d => d.count) }]}
                                    type="bar" height={height}
                                />
                                : <EmptyStateCard title="No lifecycle data" />
                        )}
                    </FocusableVisual>
                    <FocusableVisual className={COL_SECONDARY} title="Warranty Status" note={assetSelectableNote('Expiring soon = within 90 days', 'warrantyStatus')}>
                        {({ height }) => (
                            assetByWarrantyStatusRows.some(s => s.count > 0)
                                ? <Chart
                                    key={`warranty-status-${canCrossFilterAssets}`}
                                    options={canCrossFilterAssets
                                        ? assetSelectableDonutOptions(assetByWarrantyStatusRows, 'analytics-warranty-status', 'warrantyStatus', ['#e11d48', '#d97706', '#059669', CHART_INK.trackMuted])
                                        : distDonutOptions('analytics-warranty-status', assetByWarrantyStatusRows.map(d => d.label), ['#e11d48', '#d97706', '#059669', CHART_INK.trackMuted])}
                                    series={assetByWarrantyStatusRows.map(d => d.count)}
                                    type="donut" height={height}
                                />
                                : <EmptyStateCard title="No warranty data" reason="No warranty expiry dates have been recorded." />
                        )}
                    </FocusableVisual>
                </div>

                <div className={CONTENT_GRID}>
                    <FocusableVisual className={COL_PRIMARY} title="Asset Purchase Trend" note="Acquisitions per month · last 12 months">
                        {({ height }) => (
                            assetDistributions.purchaseTrend.length > 0
                                ? <Chart
                                    options={distTrendOptions('analytics-purchase-trend', assetDistributions.purchaseTrend.map(p => formatMonthLabel(p.month)))}
                                    series={[{ name: 'Assets acquired', data: assetDistributions.purchaseTrend.map(p => p.count) }]}
                                    type="line" height={height}
                                />
                                : <EmptyStateCard title="No purchase history" reason="No assets were acquired in the last 12 months." />
                        )}
                    </FocusableVisual>
                    <FocusableVisual className={COL_SECONDARY} title="Missing / Lost Assets" note="Written off or unaccounted for">
                        {({ height }) => (
                            assetDistributions.missingAssets.some(s => s.count > 0)
                                ? <Chart
                                    options={distBarOptions('analytics-missing-assets', assetDistributions.missingAssets.map(d => d.label))}
                                    series={[{ name: 'Assets', data: assetDistributions.missingAssets.map(d => d.count) }]}
                                    type="bar" height={height}
                                />
                                : <EmptyStateCard icon={<CheckCircle size={20} />} title="No lost or stolen assets." />
                        )}
                    </FocusableVisual>
                </div>

                <FocusableVisual title="Warranty Expiry Trend" note="Assets falling out of warranty · next 12 months">
                    {({ height }) => (
                        assetDistributions.warrantyExpiryTrend.length > 0
                            ? <Chart
                                options={distTrendOptions('analytics-warranty-expiry-trend', assetDistributions.warrantyExpiryTrend.map(p => formatMonthLabel(p.month)), ['#d97706'])}
                                series={[{ name: 'Warranties expiring', data: assetDistributions.warrantyExpiryTrend.map(p => p.count) }]}
                                type="line" height={height}
                            />
                            : <EmptyStateCard title="No upcoming expiries" reason="No asset warranty expires in the next 12 months." />
                    )}
                </FocusableVisual>

                <FocusableVisual title="Assets Repaired Over Time" note="Maintenance events per month · last 12 months">
                    {({ height }) => {
                        const { months, values } = alignTrendMonths(assetDistributions.repairsStarted, assetDistributions.repairsCompleted);
                        return months.length > 0
                            ? <Chart
                                options={distTrendOptions('analytics-repair-trend', months.map(formatMonthLabel), ['#d97706', '#059669'])}
                                series={[{ name: 'Repairs started', data: values[0] }, { name: 'Repairs completed', data: values[1] }]}
                                type="line" height={height}
                            />
                            : <EmptyStateCard title="No repair activity" reason="No maintenance was logged against these assets in the last 12 months." />;
                    }}
                </FocusableVisual>

                {/* Monetary, so it rides on the financial-gated endpoint and is
                    replaced by an explanation rather than hidden when denied. */}
                <FocusableVisual title="Asset Value by Department" note={canViewFinancial ? 'Book value after depreciation' : undefined}>
                    {({ height }) => {
                        if (!canViewFinancial) {
                            return <EmptyStateCard title="Requires financial access permission" reason="Ask an administrator to grant analytics.financial.view." />;
                        }
                        const rows = assetFinancial?.valueByDepartment || [];
                        return rows.length > 0
                            ? <Chart
                                options={{
                                    ...distHBarOptions('analytics-asset-value-by-department', rows.map(r => r.department)),
                                    tooltip: { theme: CHART_TOOLTIP_THEME, y: { formatter: (v: number) => formatDisplayAmount(v) } },
                                }}
                                series={[{ name: 'Book value', data: rows.map(r => Math.round(r.value)) }]}
                                type="bar" height={Math.max(height, rows.length * 34)}
                            />
                            : <EmptyStateCard title="No value data" reason="No department's assets carry a current book value." />;
                    }}
                </FocusableVisual>
                {canViewFinancial && !!assetFinancial?.valueByDepartmentMissingRateCurrencies?.length && (
                    <p className="analytics-kpi-sub text-muted-foreground">
                        Asset value excludes currencies with no configured rate: {assetFinancial.valueByDepartmentMissingRateCurrencies.join(', ')}
                    </p>
                )}
            </>)}
            <UnavailableMetricsCard metrics={ASSET_UNAVAILABLE_METRICS} />
        </div>
    );

    /* ── Story 107 — effective chart data for the cross-filterable Licences
       charts: the live fact-table view when cross-filtering is possible,
       falling back to Story 105's static distributions (unaffected by
       selection) otherwise — the same tab that rendered before this feature
       existed, minus the "click to filter" affordance. bySoftware/byVendor/
       byType's static `count` already holds seats, from the earlier fix, so
       totalSeats: d.count matches the cross-filter shape exactly. ── */
    const licenseBySoftwareRows: LicenseAnalyticsBreakdownRow[] = canCrossFilterLicenses
        ? licenseView!.bySoftware
        : (licenseDistributions?.bySoftware || []).map(d => ({ key: d.label, label: d.label, count: d.count, totalSeats: d.count }));
    const licenseByVendorRows: LicenseAnalyticsBreakdownRow[] = canCrossFilterLicenses
        ? licenseView!.byVendor
        : (licenseDistributions?.byVendor || []).map(d => ({ key: d.label, label: d.label, count: d.count, totalSeats: d.count }));
    const licenseByTypeRows: LicenseAnalyticsBreakdownRow[] = canCrossFilterLicenses
        ? licenseView!.byType
        : (licenseDistributions?.byType || []).map(d => ({ key: d.label, label: d.label, count: d.count, totalSeats: d.count }));
    const licensePurchasedVsAssigned = canCrossFilterLicenses ? licenseView!.purchasedVsAssigned : (licenseDistributions?.purchasedVsAssigned || []);
    const licenseOverVsUnder = canCrossFilterLicenses ? licenseView!.overVsUnderLicensed : (licenseDistributions?.overVsUnderLicensed || []);
    const licensePurchaseTrend = canCrossFilterLicenses ? licenseView!.purchaseTrend : (licenseDistributions?.purchaseTrend || []);
    const licenseExpiryTimeline = canCrossFilterLicenses ? licenseView!.expiryTimeline : (licenseDistributions?.expiryTimeline || []);

    const renderLicenses = () => (
        <div className="analytics-panel">
            {/* Story 107 — cross-filtering. Selection lives on this tab only
                (not the page's own AnalyticsAdvancedFilters), so its chips get
                their own bar, matching how the Cloud tab surfaces its selection.
                Placed above the KPI strip so an active selection is the first
                thing read on the tab, not something scrolled past. */}
            {licenseFactsLoading && !canCrossFilterLicenses && (
                <p className="text-xs text-muted-foreground">Enabling click-to-filter on the charts below…</p>
            )}
            {canCrossFilterLicenses && licenseFilterChips.length > 0 && (
                <div className="rounded-md border bg-card">
                    <div className="flex items-center justify-end gap-3 px-4 pt-3">
                        <span className="text-xs text-muted-foreground">
                            {fmt(licenseView!.scopedCount)} of {fmt(licenseFacts!.facts.length)} licenses
                        </span>
                    </div>
                    <FilterChipsBar chips={licenseFilterChips} onClearAll={() => setLicenseSelection({})} />
                </div>
            )}
            <div className={KPI_GRID}>
                <KpiCard icon={<Key size={18} />} label="Total Licenses" value={num(licenses?.total)} accent="#7c3aed" iconBg="rgba(124,58,237,0.10)" kpiId="kpi-license-seats" onClick={setActiveKpiId} />
                <KpiCard icon={<CheckCircle size={18} />} label="Seats Assigned" value={num(licenses?.assigned)} sub="In use" accent="#059669" iconBg="rgba(13,148,136,0.12)" kpiId="kpi-license-seats" onClick={setActiveKpiId} />
                <KpiCard icon={<Package size={18} />} label="Seats Available" value={num(licenses?.available)} sub="Free to assign" accent="#4f46e5" iconBg="rgba(79,70,229,0.10)" kpiId="kpi-license-seats" onClick={setActiveKpiId} />
                <KpiCard icon={<TrendingDown size={18} />} label="Expired" value={num(licenses?.expired)} sub="Need renewal" accent="#e11d48" iconBg="rgba(225,29,72,0.10)" kpiId="kpi-license-expiry" onClick={setActiveKpiId} />
                <KpiCard icon={<AlertTriangle size={18} />} label="Expiring Soon" value={num(licenses?.expiringSoon)} sub="Within 30 days" accent="#d97706" iconBg="rgba(217,119,6,0.10)" kpiId="kpi-license-expiry" onClick={setActiveKpiId} />
                <KpiCard icon={<Activity size={18} />} label="Compliance" value={formatPercent(num(licenses?.compliancePercentage))} sub="Seat utilization" accent="#0891b2" iconBg="#ecfeff" />
                {canViewFinancial && (
                    <KpiCard
                        icon={<DollarSign size={18} />} label="Estimated Unused License Cost"
                        value={licenseCostLoading && !licenseCost ? '…' : formatAvailability(licenseCost?.estimatedUnusedLicenseCost, formatDisplayAmount).display}
                        sub={licenseCost?.estimatedUnusedLicenseCost.missingRateCurrencies?.length
                            ? `Excludes currencies with no rate: ${licenseCost.estimatedUnusedLicenseCost.missingRateCurrencies.join(', ')}`
                            : formatAvailability(licenseCost?.estimatedUnusedLicenseCost, formatDisplayAmount).reason}
                        accent="#e11d48" iconBg="rgba(225,29,72,0.10)"
                    />
                )}
                {canViewFinancial && (
                    <KpiCard
                        icon={<Gauge size={18} />} label="Cost per Assigned Seat"
                        value={licenseCostLoading && !licenseCost ? '…' : formatAvailability(licenseCost?.costPerAssignedSeat, formatDisplayAmount).display}
                        sub={licenseCost?.costPerAssignedSeat.missingRateCurrencies?.length
                            ? `Excludes currencies with no rate: ${licenseCost.costPerAssignedSeat.missingRateCurrencies.join(', ')}`
                            : formatAvailability(licenseCost?.costPerAssignedSeat, formatDisplayAmount).reason}
                        accent="#0891b2" iconBg="#ecfeff"
                    />
                )}
            </div>

            <div className={CONTENT_GRID}>
                <FocusableVisual className={COL_PRIMARY} title="License Seat Utilization (Top Titles)" note={`% of seats assigned · largest ${licenseUtil.length} by seat count`}>
                    {({ height, focused }) => (
                        luLabels.length > 0
                            ? <div className={cn(!focused && 'max-h-[420px] overflow-y-auto')}>
                                <Chart
                                    options={{ ...luBarOptions, xaxis: { ...luBarOptions.xaxis, categories: luLabels } }}
                                    series={[{ name: 'Utilization', data: luUtilizationPct }]}
                                    type="bar" height={Math.max(height, luLabels.length * 44)}
                                />
                            </div>
                            : <EmptyStateCard title="No license data" reason="No software titles have seats assigned yet." />
                    )}
                </FocusableVisual>
                <FocusableVisual className={COL_SECONDARY} title="Seat Overview">
                    {() => (<>
                        <div className="py-2">
                            <ProgressRow label="Used Seats" value={num(licenses?.assigned)} total={num(licenses?.assigned) + num(licenses?.available)} color="#4f46e5" />
                            <ProgressRow label="Available Seats" value={num(licenses?.available)} total={num(licenses?.assigned) + num(licenses?.available)} color="#0d9488" />
                            <ProgressRow label="Expired Licenses" value={num(licenses?.expired)} total={num(licenses?.total)} color="#e11d48" />
                            <ProgressRow label="Expiring in 30d" value={num(licenses?.expiringSoon)} total={num(licenses?.total)} color="#d97706" />
                        </div>
                        <div className="license-compliance-badge">
                            <span className="compliance-label">Compliance Score</span>
                            <span className={`compliance-score ${num(licenses?.compliancePercentage) >= 80 ? 'good' : 'warn'}`}>
                                {formatPercent(num(licenses?.compliancePercentage))}
                            </span>
                        </div>
                    </>)}
                </FocusableVisual>
            </div>

            {/* Phase 3 — License cost/compliance KPIs (#35, #36, #39, #40). Live-only.
                The two summary KPI cards live in the top KPI_GRID now; this card is
                only the permission/loading gate for the charts immediately below. */}
            {!canViewFinancial ? (
                <Card>
                    <CardContent className="p-5">
                        <SectionHead title="Cost & Compliance" note="Live data — no historical trend yet" />
                        <EmptyStateCard title="Requires financial access permission" reason="Ask an administrator to grant analytics.financial.view." />
                    </CardContent>
                </Card>
            ) : licenseCostLoading && !licenseCost ? (
                <Card>
                    <CardContent className="p-5">
                        <SectionHead title="Cost & Compliance" note="Live data — no historical trend yet" />
                        <EmptyStateCard title="Loading cost KPIs…" spinning />
                    </CardContent>
                </Card>
            ) : !licenseCost ? (
                <Card>
                    <CardContent className="p-5">
                        <SectionHead title="Cost & Compliance" note="Live data — no historical trend yet" />
                        <EmptyStateCard title="Cost KPIs unavailable" reason="This data could not be loaded for the current filters." />
                    </CardContent>
                </Card>
            ) : null}
            {licenseCost && canViewFinancial && (
                <div className={CONTENT_GRID}>
                    <FocusableVisual className={COL_PRIMARY} title="Annualized Cost by Vendor" note={licenseSelectionIds ? 'Scoped to the current selection above' : undefined}>
                        {({ height }) => (
                            licenseCost.costByVendor.length > 0
                                ? <Chart
                                    options={{ ...catBarOptions, chart: { ...catBarOptions.chart, id: 'analytics-cost-by-vendor' }, xaxis: { ...catBarOptions.xaxis, categories: licenseCost.costByVendor.map(v => v.vendor) } }}
                                    series={[{ name: 'Annualized Cost', data: licenseCost.costByVendor.map(v => v.annualizedCost) }]}
                                    type="bar" height={height}
                                />
                                : <EmptyStateCard title="No vendor data" />
                        )}
                    </FocusableVisual>
                    <FocusableVisual className={COL_SECONDARY} title="Compliance Risk Exposure" note={licenseSelectionIds ? 'Scoped to the current selection above' : undefined}>
                        {() => (
                            licenseCost.complianceRiskExposure.length > 0 ? (
                                <Table>
                                    <TableHeader><TableRow><TableHead>Risk</TableHead><TableHead>Licenses</TableHead><TableHead>Cost</TableHead></TableRow></TableHeader>
                                    <TableBody>
                                        {licenseCost.complianceRiskExposure.map((r) => (
                                            <TableRow
                                                key={r.risk}
                                                className={cn(r.risk !== 'low' && 'cursor-pointer hover:bg-muted/40')}
                                                onClick={r.risk !== 'low' ? () => setActiveKpiId('kpi-compliance-risk') : undefined}
                                            >
                                                <TableCell className="flex items-center gap-1.5">
                                                    <ShieldCheck size={14} className={r.risk === 'high' ? 'text-destructive' : r.risk === 'medium' ? 'text-warning' : 'text-muted-foreground'} />
                                                    {r.risk}
                                                </TableCell>
                                                <TableCell className="tabular-nums">{fmt(r.licenseCount)}</TableCell>
                                                <TableCell className="tabular-nums">{formatDisplayAmount(r.annualizedCost)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : <EmptyStateCard title="No compliance risk data" />
                        )}
                    </FocusableVisual>
                </div>
            )}
            {canViewFinancial && (
                <FocusableVisual title="Top Expensive Licenses" note={licenseSelectionIds ? 'By total cost · scoped to the current selection above' : 'By total cost'}>
                    {() => (<>
                        <div className="flex items-center justify-end gap-2 pb-3">
                            <label htmlFor="top-expensive-limit" className="text-xs font-medium text-muted-foreground">Show</label>
                            <select
                                id="top-expensive-limit"
                                value={topExpensiveLimit}
                                onChange={(e) => setTopExpensiveLimit(Number(e.target.value))}
                                className="h-8 rounded-md border border-input bg-background px-2 text-xs font-medium"
                            >
                                {[5, 10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </div>
                        {licenseCostLoading && !licenseCost ? (
                            <EmptyStateCard title="Loading…" spinning />
                        ) : licenseCost && licenseCost.topExpensive.length > 0 ? (
                            <Table>
                                <TableHeader><TableRow><TableHead>Software / Plan</TableHead><TableHead>Total Cost</TableHead></TableRow></TableHeader>
                                <TableBody>
                                    {licenseCost.topExpensive.map((r, i) => (
                                        <TableRow key={`${r.softwareName}-${i}`}>
                                            <TableCell>{r.softwareName}</TableCell>
                                            <TableCell className="tabular-nums">{r.totalCost.toLocaleString(undefined, { style: 'currency', currency: r.currency || 'USD' })}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : <EmptyStateCard title="No license cost data" />}
                    </>)}
                </FocusableVisual>
            )}

            {/* Story 105/107 — License distribution breakdowns (software, vendor, type,
                status, seats, expiry/purchase/renewal trends, allocation by user,
                over/under-licensed). Click-to-filter (Story 107) when the fact table
                is available; otherwise these fall back to Story 105's static,
                non-clickable aggregates — same charts, no selection affordance.
                No header card while loaded — each chart below carries its own title,
                so an empty banner card added nothing once the data was in. */}
            {licenseDistributionsLoading && !licenseDistributions ? (
                <Card>
                    <CardContent className="p-5">
                        <EmptyStateCard title="Loading license breakdowns…" spinning />
                    </CardContent>
                </Card>
            ) : !licenseDistributions ? (
                <Card>
                    <CardContent className="p-5">
                        <EmptyStateCard title="License breakdowns unavailable" reason="This data could not be loaded for the current filters." />
                    </CardContent>
                </Card>
            ) : null}
            {licenseDistributions && (<>
                <div className={CONTENT_GRID}>
                    <FocusableVisual className={COL_PRIMARY} title="Licenses by Software/Product" note={licenseSelectableNote(`Seats · top ${licenseDistributions.topN}`, 'software')}>
                        {({ height }) => (
                            licenseBySoftwareRows.length > 0
                                ? <Chart
                                    // react-apexcharts creates the underlying ApexCharts
                                    // instance once on mount and only patches it afterward;
                                    // ApexCharts itself does not rebind `chart.events` on an
                                    // options update, only at construction. Without a key
                                    // tied to interactivity, a chart that first rendered
                                    // non-interactive (facts still loading) would stay
                                    // permanently unclickable once facts arrive and the
                                    // options switch to the selectable variant. The key
                                    // forces a fresh mount — and a fresh ApexCharts instance
                                    // with events correctly bound — exactly when that happens.
                                    key={`software-${canCrossFilterLicenses}`}
                                    options={canCrossFilterLicenses
                                        ? licenseSelectableHBarOptions(licenseBySoftwareRows, 'analytics-licenses-by-software', 'software')
                                        : distHBarOptions('analytics-licenses-by-software', licenseBySoftwareRows.map(d => d.label))}
                                    series={[{ name: 'Seats', data: licenseBySoftwareRows.map(d => d.totalSeats) }]}
                                    type="bar" height={Math.max(height, licenseBySoftwareRows.length * 34)}
                                />
                                : <EmptyStateCard title="No software data" />
                        )}
                    </FocusableVisual>
                    <FocusableVisual className={COL_SECONDARY} title="Licenses by Vendor" note={licenseSelectableNote(`Seats · top ${licenseDistributions.topN}`, 'vendor')}>
                        {({ height }) => (
                            licenseByVendorRows.length > 0
                                ? <Chart
                                    // See the "by Software" chart above for why this key exists.
                                    key={`vendor-${canCrossFilterLicenses}`}
                                    options={canCrossFilterLicenses
                                        ? licenseSelectableBarOptions(licenseByVendorRows, 'analytics-licenses-by-vendor', 'vendor')
                                        : distBarOptions('analytics-licenses-by-vendor', licenseByVendorRows.map(d => d.label))}
                                    series={[{ name: 'Seats', data: licenseByVendorRows.map(d => d.totalSeats) }]}
                                    type="bar" height={height}
                                />
                                : <EmptyStateCard title="No vendor data" />
                        )}
                    </FocusableVisual>
                </div>

                <div className={CONTENT_GRID}>
                    <FocusableVisual className={COL_PRIMARY} title="Licenses by Type" note={licenseSelectableNote('Seats · user-based vs device-based', 'type')}>
                        {({ height }) => (
                            licenseByTypeRows.some(d => d.totalSeats > 0)
                                ? <Chart
                                    // See the "by Software" chart above for why this key exists.
                                    key={`type-${canCrossFilterLicenses}`}
                                    options={canCrossFilterLicenses
                                        ? licenseSelectableDonutOptions(licenseByTypeRows, 'analytics-licenses-by-type', 'type')
                                        : distDonutOptions('analytics-licenses-by-type', licenseByTypeRows.map(d => d.label))}
                                    series={licenseByTypeRows.map(d => d.totalSeats)}
                                    type="donut" height={height}
                                />
                                : <EmptyStateCard title="No type data" />
                        )}
                    </FocusableVisual>
                    <FocusableVisual className={COL_SECONDARY} title="Licenses by Status" note={canCrossFilterLicenses ? licenseSelectableNote(undefined, 'status') : undefined}>
                        {({ height }) => (
                            !canCrossFilterLicenses
                                ? <EmptyStateCard title="Not available" reason="This breakdown needs the fact table, which is unavailable above the row cap or while data is still loading." />
                                : licenseView!.byStatus.length > 0
                                    ? <Chart
                                        options={licenseSelectableDonutOptions(licenseView!.byStatus, 'analytics-licenses-by-status', 'status')}
                                        series={licenseView!.byStatus.map(d => d.count)}
                                        type="donut" height={height}
                                    />
                                    : <EmptyStateCard title="No status data" />
                        )}
                    </FocusableVisual>
                </div>

                <div className={CONTENT_GRID}>
                    <FocusableVisual className={COL_PRIMARY} title="Purchased vs Assigned Seats" note="Assigned bar turns red once it crosses the purchased-seats line — over-allocated">
                        {({ height }) => {
                            // Story 109 — a part-to-whole donut can't represent this once
                            // Assigned exceeds Purchased (over-allocated licenses, which the
                            // product already tracks and alerts on separately), so Assigned
                            // renders as the bar and Purchased as a target line instead.
                            const purchased = licensePurchasedVsAssigned.find(d => d.label === 'Purchased')?.count ?? 0;
                            const assigned = licensePurchasedVsAssigned.find(d => d.label === 'Assigned')?.count ?? 0;
                            return (purchased + assigned) > 0
                                ? <Chart
                                    options={varianceBarOptions(
                                        'analytics-purchased-vs-assigned',
                                        [{ label: 'Seats', actual: assigned, target: purchased }],
                                        CHART_INK, CHART_TOOLTIP_THEME, 'Assigned', 'Purchased',
                                    )}
                                    series={[{ name: 'Assigned', data: [assigned] }]}
                                    type="bar" height={height}
                                />
                                : <EmptyStateCard title="No seat data" />;
                        }}
                    </FocusableVisual>
                    <FocusableVisual className={COL_SECONDARY} title="Over- vs Under-Licensed Software" note="Assigned seats compared to purchased seats">
                        {({ height }) => (
                            licenseOverVsUnder.some(d => d.count > 0)
                                ? <Chart
                                    // Story 109 — a fixed semantic palette (red = cost waste,
                                    // amber = compliance risk, green = healthy) replaces the
                                    // rotating category palette, the same convention Warranty
                                    // Status already uses for its fixed-order buckets.
                                    options={{
                                        ...distBarOptions('analytics-over-under-licensed', licenseOverVsUnder.map(d => d.label)),
                                        colors: licenseOverVsUnder.map(d => (
                                            d.label === 'Over-licensed' ? '#e11d48'
                                                : d.label === 'Under-licensed' ? '#d97706'
                                                    : '#059669'
                                        )),
                                        // `distributed` is required for a per-category `colors`
                                        // array to apply at all — without it every bar in a
                                        // single series takes colors[0]. Legend is suppressed
                                        // the same way every other distributed chart in this
                                        // file already does (it would otherwise legend every bar).
                                        plotOptions: { bar: { borderRadius: 6, borderRadiusApplication: 'end', columnWidth: '55%', distributed: true } },
                                        legend: { show: false },
                                    }}
                                    series={[{ name: 'Licenses', data: licenseOverVsUnder.map(d => d.count) }]}
                                    type="bar" height={height}
                                />
                                : <EmptyStateCard title="No allocation data" />
                        )}
                    </FocusableVisual>
                </div>

                <div className={CONTENT_GRID}>
                    <FocusableVisual className={COL_PRIMARY} title="License Allocation by User" note={`Top ${licenseDistributions.topN} assignees`}>
                        {({ height }) => (
                            licenseDistributions.allocationByUser.length > 0
                                ? <Chart
                                    options={distHBarOptions('analytics-license-allocation-by-user', licenseDistributions.allocationByUser.map(d => d.label))}
                                    series={[{ name: 'Seats', data: licenseDistributions.allocationByUser.map(d => d.count) }]}
                                    type="bar" height={Math.max(height, licenseDistributions.allocationByUser.length * 34)}
                                />
                                : <EmptyStateCard title="No assignment data" reason="No license seats have been assigned to a user yet." />
                        )}
                    </FocusableVisual>
                    <FocusableVisual className={COL_SECONDARY} title="Expired Licenses by Vendor">
                        {({ height }) => (
                            licenseDistributions.expiredByVendor.length > 0
                                ? <Chart
                                    options={distBarOptions('analytics-expired-by-vendor', licenseDistributions.expiredByVendor.map(d => d.label))}
                                    series={[{ name: 'Expired', data: licenseDistributions.expiredByVendor.map(d => d.count) }]}
                                    type="bar" height={height}
                                />
                                : <EmptyStateCard title="No expired licenses" />
                        )}
                    </FocusableVisual>
                </div>

                <div className={CONTENT_GRID}>
                    <FocusableVisual className={COL_PRIMARY} title="License Expiry Timeline" note="Licenses expiring per month · next 12 months">
                        {({ height }) => (
                            licenseExpiryTimeline.length > 0
                                ? <Chart
                                    options={distTrendOptions('analytics-license-expiry-timeline', licenseExpiryTimeline.map(p => formatMonthLabel(p.month)), ['#e11d48'])}
                                    series={[{ name: 'Licenses expiring', data: licenseExpiryTimeline.map(p => p.count) }]}
                                    type="line" height={height}
                                />
                                : <EmptyStateCard title="Nothing expiring in the next 12 months" />
                        )}
                    </FocusableVisual>
                    <FocusableVisual className={COL_SECONDARY} title="License Purchase Trend" note="Purchases per month · last 12 months">
                        {({ height }) => (
                            licensePurchaseTrend.length > 0
                                ? <Chart
                                    options={distTrendOptions('analytics-license-purchase-trend', licensePurchaseTrend.map(p => formatMonthLabel(p.month)))}
                                    series={[{ name: 'Licenses purchased', data: licensePurchaseTrend.map(p => p.count) }]}
                                    type="line" height={height}
                                />
                                : <EmptyStateCard title="No purchases recorded in the last 12 months" />
                        )}
                    </FocusableVisual>
                </div>

                <FocusableVisual title="License Renewal Trend" note="Renewals recorded per month · last 12 months">
                    {({ height }) => (
                        licenseDistributions.renewalTrend.length > 0
                            ? <Chart
                                options={distTrendOptions('analytics-license-renewal-trend', licenseDistributions.renewalTrend.map(p => formatMonthLabel(p.month)), ['#059669'])}
                                series={[{ name: 'Renewals', data: licenseDistributions.renewalTrend.map(p => p.count) }]}
                                type="line" height={height}
                            />
                            : <EmptyStateCard title="No renewals recorded in the last 12 months" />
                    )}
                </FocusableVisual>

                <FocusableVisual title="Upcoming Renewals" note={`Next ${licenseDistributions.upcomingRenewals.length} by soonest date`}>
                    {() => (
                        licenseDistributions.upcomingRenewals.length > 0 ? (
                            <Table>
                                <TableHeader><TableRow><TableHead>Software / Plan</TableHead><TableHead>Vendor</TableHead><TableHead>Next Renewal</TableHead></TableRow></TableHeader>
                                <TableBody>
                                    {licenseDistributions.upcomingRenewals.map((r, i) => (
                                        <TableRow key={`${r.softwareName}-${i}`}>
                                            <TableCell>{r.softwareName}</TableCell>
                                            <TableCell>{r.vendor}</TableCell>
                                            <TableCell className="tabular-nums">{new Date(r.nextRenewalDate).toLocaleDateString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : <EmptyStateCard title="No upcoming renewals" reason="No license has a next renewal date recorded." />
                    )}
                </FocusableVisual>
            </>)}
            <UnavailableMetricsCard metrics={LICENSE_UNAVAILABLE_METRICS} />
        </div>
    );

    const renderInventory = () => (
        <div className="analytics-panel">
            <div className={KPI_GRID}>
                <KpiCard icon={<Layers size={18} />} label="Total Stock Units" value={num(inventory?.totalStockUnits)} accent="#4f46e5" iconBg="rgba(79,70,229,0.10)" kpiId="kpi-stock-health" onClick={setActiveKpiId} />
                <KpiCard icon={<CheckCircle size={18} />} label="Available Units" value={num(inventory?.availableUnits)} sub="Ready to issue" accent="#059669" iconBg="rgba(13,148,136,0.12)" kpiId="kpi-stock-health" onClick={setActiveKpiId} />
                <KpiCard icon={<AlertTriangle size={18} />} label="Low Stock Alerts" value={num(inventory?.lowStockAlerts)} sub="Below reorder point" accent="#d97706" iconBg="rgba(217,119,6,0.10)" kpiId="kpi-low-stock" onClick={setActiveKpiId} />
                <KpiCard icon={<XCircle size={18} />} label="Out of Stock" value={num(inventory?.outOfStock)} sub="Zero available" accent="#e11d48" iconBg="rgba(225,29,72,0.10)" kpiId="kpi-low-stock" onClick={setActiveKpiId} />
                <KpiCard icon={<RotateCcw size={18} />} label="Refundable Items" value={refundable} accent="#7c3aed" iconBg="rgba(124,58,237,0.10)" />
                <KpiCard icon={<Package size={18} />} label="Non-Refundable" value={nonRefundable} accent="#0891b2" iconBg="#ecfeff" />
            </div>
            <div className={CONTENT_GRID}>
                <FocusableVisual className={COL_PRIMARY} title="Stock Health Overview">
                    {() => (
                        <div className="py-3">
                            <ProgressRow label="Total Stock" value={num(inventory?.totalStockUnits)} total={num(inventory?.totalStockUnits)} color="#4f46e5" />
                            <ProgressRow label="Available" value={num(inventory?.availableUnits)} total={num(inventory?.totalStockUnits)} color="#0d9488" />
                            <ProgressRow label="Low Stock Items" value={num(inventory?.lowStockAlerts)} total={num(inventory?.totalStockUnits)} color="#d97706" />
                            <ProgressRow label="Out of Stock Items" value={num(inventory?.outOfStock)} total={num(inventory?.totalStockUnits)} color="#e11d48" />
                        </div>
                    )}
                </FocusableVisual>
                <FocusableVisual className={COL_SECONDARY} title="Refundable vs Non-Refundable">
                    {({ height }) => (
                        (refundable + nonRefundable) > 0
                            ? <Chart options={inventoryDonutOptions} series={[refundable, nonRefundable]} type="donut" height={height} />
                            : <EmptyStateCard title="No data" />
                    )}
                </FocusableVisual>
            </div>
            <FocusableVisual title="Stock Movement This Period" note="Change vs last period — green is up, red is down">
                {({ height }) => (
                    // Story 109 — one diverging bar per reason (this − last) reads
                    // directly as "what moved", instead of two bar heights the
                    // reader had to subtract by eye. Both raw counts stay in the tooltip.
                    <Chart
                        options={deltaBarOptions('analytics-stock-movement', movLabels, smThis, smLast, CHART_INK, CHART_TOOLTIP_THEME)}
                        series={[{ name: 'Change', data: movLabels.map((_, i) => smThis[i] - smLast[i]) }]}
                        type="bar" height={height}
                    />
                )}
            </FocusableVisual>

            {/* Phase 3 — Inventory activity KPIs (#49, #50). Live-only. */}
            <Card>
                <CardContent className="p-5 pb-0">
                    <SectionHead title="Activity & Returns" note="Live data — no historical trend yet" />
                </CardContent>
                <CardContent className={cn('p-5 pt-0', (inventoryActivityLoading || !inventoryActivity) && 'p-0')}>
                    {inventoryActivityLoading && !inventoryActivity ? (
                        <EmptyStateCard title="Loading activity KPIs…" spinning />
                    ) : !inventoryActivity ? (
                        <EmptyStateCard title="Activity KPIs unavailable" reason="This data could not be loaded for the current filters." />
                    ) : (
                        <div className={KPI_GRID}>
                            <KpiCard
                                icon={<Clock size={18} />} label="Overdue Refundable Returns"
                                value={num(inventoryActivity.overdueRefundableReturns)}
                                sub="Refundable items past expected return date"
                                accent="#e11d48" iconBg="rgba(225,29,72,0.10)"
                                kpiId="kpi-overdue-refundable" onClick={setActiveKpiId}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>
            {inventoryActivity && (
                <div className={CONTENT_GRID}>
                    <FocusableVisual
                        className={COL_PRIMARY}
                        title="Most Frequently Issued Items"
                        note={inventoryActivity.period
                            ? `${new Date(inventoryActivity.period.startDate).toLocaleDateString()} – ${new Date(inventoryActivity.period.endDate).toLocaleDateString()}`
                            : 'Last 90 days'}
                    >
                        {({ height }) => (
                            inventoryActivity.mostFrequentlyIssued.length > 0
                                ? <Chart
                                    options={{ ...catBarOptions, chart: { ...catBarOptions.chart, id: 'analytics-most-frequently-issued' }, xaxis: { ...catBarOptions.xaxis, categories: inventoryActivity.mostFrequentlyIssued.map(i => i.itemName) } }}
                                    series={[{ name: 'Units Issued', data: inventoryActivity.mostFrequentlyIssued.map(i => i.totalIssued) }]}
                                    type="bar" height={height}
                                />
                                : <EmptyStateCard title="No issuance data" reason={`No items have been issued in the selected period${inventoryActivity.period ? '' : ' (last 90 days)'}.`} />
                        )}
                    </FocusableVisual>
                    <FocusableVisual className={COL_SECONDARY} title="Overdue Refundable Returns">
                        {({ focused }) => (
                            inventoryActivity.overdueRefundableItems.length > 0 ? (
                                <div className={cn(!focused && 'max-h-[300px] overflow-y-auto')}>
                                    <Table>
                                        <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Qty</TableHead><TableHead>Due</TableHead></TableRow></TableHeader>
                                        <TableBody>
                                            {inventoryActivity.overdueRefundableItems.slice(0, 8).map((r) => (
                                                <TableRow key={r.id} className="cursor-pointer hover:bg-muted/40" onClick={() => setActiveKpiId('kpi-overdue-refundable')}>
                                                    <TableCell>{r.itemName}</TableCell>
                                                    <TableCell className="tabular-nums">{r.quantity}</TableCell>
                                                    <TableCell className="text-muted-foreground text-xs">{new Date(r.expectedReturnDate).toLocaleDateString()}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            ) : <EmptyStateCard icon={<CheckCircle size={20} />} title="No overdue refundable returns." />
                        )}
                    </FocusableVisual>
                </div>
            )}
        </div>
    );

    const panelMap: Record<TabKey, () => React.ReactNode> = {
        executive: renderExecutive,
        assets: renderAssets,
        licenses: renderLicenses,
        inventory: renderInventory,
        // Owns its own fetch — the page filters (location, category, asset date
        // range) do not apply to a cloud resource, so only Refresh reaches it.
        cloud: () => (
            <CloudAnalyticsPanel
                refreshTick={cloudRefreshTick}
                onData={setCloudAnalytics}
                onFilterControl={setCloudFilterControl}
            />
        ),
    };

    /* ══════ CSV EXPORT — flattens the active tab's already-fetched data ══════ */
    const handleExportCsv = () => {
        const tabLabel = TABS.find(t => t.key === activeTab)?.label || activeTab;
        let headers: string[] = [];
        let rows: (string | number)[][] = [];

        switch (activeTab) {
            case 'executive':
                headers = ['Exception', 'Count'];
                rows = alerts ? [
                    ['Warranty Expired', alerts.warrantyExpired],
                    ['Warranty Expiring (30d)', alerts.warrantyExpiring30],
                    ['Expired Licenses', alerts.expiredLicenses],
                    ['Licenses Expiring (30d)', alerts.licensesExpiring30],
                    ['Over-Allocated Licenses', alerts.overAllocatedLicenses],
                    ['Out-of-Stock Items', alerts.outOfStock],
                    ['Low-Stock Items', alerts.lowStockItems],
                    ['Overdue Returns', alerts.overdueReturns],
                    ['Damaged Returns', alerts.damagedReturns],
                    ['Unassigned Assets', alerts.assetsUnassigned],
                ].filter(([, v]) => (v as number) > 0) : [];
                break;
            case 'assets': {
                // One row per slice of every breakdown, same shape as the Cloud
                // export below, so the file carries the whole tab (Story 104's
                // full distribution set) rather than just the status donut.
                headers = ['Breakdown', 'Label', 'Count'];
                const statusRows: [string, string, number][] = (assets?.byStatus || []).map(s => {
                    const count = parseInt(s.count);
                    return ['Status', s.status, count];
                });
                const distGroups: [string, { label: string; count: number }[]][] = assetDistributions ? [
                    ['Department', assetDistributions.byDepartment],
                    ['Assigned User', assetDistributions.byAssignedUser],
                    ['Business Owner', assetDistributions.byBusinessOwner],
                    ['Manufacturer', assetDistributions.byManufacturer],
                    ['Vendor', assetDistributions.byVendor],
                    ['Model', assetDistributions.byModel],
                    ['Age', assetDistributions.byAge],
                    ['Warranty Status', assetDistributions.byWarrantyStatus],
                    ['Assigned vs Unassigned', assetDistributions.assignedVsUnassigned],
                    ['Lifecycle Stage', assetDistributions.byLifecycleStage],
                    ['Missing / Lost', assetDistributions.missingAssets],
                ] : [];
                const trendGroups: [string, { month: string; count: number }[]][] = assetDistributions ? [
                    ['Warranty Expiry Trend', assetDistributions.warrantyExpiryTrend],
                    ['Purchase Trend', assetDistributions.purchaseTrend],
                    ['Repairs Started', assetDistributions.repairsStarted],
                    ['Repairs Completed', assetDistributions.repairsCompleted],
                ] : [];
                rows = [
                    ...statusRows,
                    ...distGroups.flatMap(([group, list]) => list.map(r => [group, r.label, r.count] as [string, string, number])),
                    ...trendGroups.flatMap(([group, list]) => list.map(r => [group, formatMonthLabel(r.month), r.count] as [string, string, number])),
                ];
                break;
            }
            case 'licenses': {
                // Same full-breakdown-set pattern as Assets/Cloud, built from
                // Story 105's license-distributions payload plus the existing
                // per-title seat utilization table and upcoming renewals list.
                headers = ['Breakdown', 'Label', 'Count'];
                const utilRows: [string, string, number][] = licenseUtil.map(l => [
                    'Seat Utilization (Top Titles)', l.license_softwareName,
                    Math.max(0, num(l.license_totalSeats) - num(l.license_usedSeats)),
                ]);
                const distGroups: [string, { label: string; count: number }[]][] = licenseDistributions ? [
                    ['Software / Product (Seats)', licenseDistributions.bySoftware],
                    ['Vendor (Seats)', licenseDistributions.byVendor],
                    ['Type (Seats)', licenseDistributions.byType],
                    ['Purchased vs Assigned', licenseDistributions.purchasedVsAssigned],
                    ['Over- vs Under-Licensed', licenseDistributions.overVsUnderLicensed],
                    ['Allocation by User', licenseDistributions.allocationByUser],
                    ['Expired by Vendor', licenseDistributions.expiredByVendor],
                ] : [];
                const trendGroups: [string, { month: string; count: number }[]][] = licenseDistributions ? [
                    ['Expiry Timeline', licenseDistributions.expiryTimeline],
                    ['Purchase Trend', licenseDistributions.purchaseTrend],
                    ['Renewal Trend', licenseDistributions.renewalTrend],
                ] : [];
                const renewalRows: [string, string, string][] = (licenseDistributions?.upcomingRenewals || []).map(r =>
                    ['Upcoming Renewals', `${r.softwareName} (${r.vendor})`, new Date(r.nextRenewalDate).toLocaleDateString()],
                );
                rows = [
                    ...utilRows,
                    ...distGroups.flatMap(([group, list]) => list.map(r => [group, r.label, r.count] as [string, string, number])),
                    ...trendGroups.flatMap(([group, list]) => list.map(r => [group, formatMonthLabel(r.month), r.count] as [string, string, number])),
                    ...renewalRows,
                ];
                break;
            }
            case 'inventory':
                headers = ['Metric', 'Value'];
                rows = [
                    ['Total Stock Units', num(inventory?.totalStockUnits)],
                    ['Available Units', num(inventory?.availableUnits)],
                    ['Low Stock Alerts', num(inventory?.lowStockAlerts)],
                    ['Out of Stock', num(inventory?.outOfStock)],
                    ['Refundable Items', num(inventory?.distribution?.refundable)],
                    ['Non-Refundable Items', num(inventory?.distribution?.nonRefundable)],
                ];
                break;
            case 'cloud': {
                // One row per slice of every breakdown, so the export carries
                // the whole tab rather than whichever chart happens to be first.
                headers = ['Breakdown', 'Value', 'Resources', `Monthly Cost (${cloudAnalytics?.baseCurrency || 'base'})`];
                const groups: [string, { label: string; count: number; monthlyCost: number }[]][] = cloudAnalytics ? [
                    ['Provider', cloudAnalytics.byProvider],
                    ['Environment tier', cloudAnalytics.byEnvironmentTier],
                    ['Environment', cloudAnalytics.byEnvironment],
                    ['Resource type', cloudAnalytics.byResourceType],
                    ['Region', cloudAnalytics.byRegion],
                    ['Provider state', cloudAnalytics.byProviderState],
                    ['Lifecycle status', cloudAnalytics.byLifecycleStatus],
                    ['Department', cloudAnalytics.byDepartment],
                    ['Owner', cloudAnalytics.byOwner],
                ] : [];
                rows = groups.flatMap(([group, list]) =>
                    list.map(r => [group, r.label, r.count, Math.round(r.monthlyCost * 100) / 100]),
                );
                break;
            }
        }

        if (rows.length === 0) {
            showToast('No data to export', 'warning');
            return;
        }
        exportToCsv(`analytics_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`, headers, rows);
        showToast(`${tabLabel} data exported to CSV`, 'success');
    };

    return (
        <>
            <style>{CSS}</style>
            <div className={cn('space-y-6', refreshing && 'opacity-60 pointer-events-none transition-opacity')}>

                {/* ── Header ── */}
                <PageHeader
                    title="Analytics & Reporting"
                    description={lastUpdated
                        ? `Last updated · ${lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                        : 'Comprehensive IT asset intelligence'}
                >
                    <div className="flex items-center gap-2">
                        {tabFilterFields.length > 0 && (
                            <FilterButton activeCount={activeFilterCount} onClick={openFilterDrawer} />
                        )}
                        {/* Cloud filters its own client-side fact table rather than
                            the page's filter state, so its button is published up
                            from the panel — same control, same place in the header. */}
                        {activeTab === 'cloud' && cloudFilterControl && (
                            <FilterButton
                                activeCount={cloudFilterControl.activeCount}
                                onClick={cloudFilterControl.open}
                            />
                        )}
                        <Button variant="outline" onClick={handleExportCsv}>
                            <Download size={15} className="mr-2" />
                            Export CSV
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => {
                                loadAll(activeFilters);
                                if (activeTab === 'executive') setExecRefreshTick(t => t + 1);
                                if (['assets', 'licenses', 'inventory'].includes(activeTab)) setPhase3RefreshTick(t => t + 1);
                                if (activeTab === 'cloud') setCloudRefreshTick(t => t + 1);
                            }}
                            disabled={refreshing}
                        >
                            <RefreshCw size={15} className={cn('mr-2', refreshing && 'animate-spin')} />
                            {refreshing ? 'Refreshing…' : 'Refresh'}
                        </Button>
                    </div>
                </PageHeader>

                {/* ── Applied filters ──
                    Chips only; the fields themselves live in the drawer, the
                    same arrangement the Assets/Licences/Inventory list pages use. */}
                <FilterChipsBar chips={filterChips} onClearAll={clearFilters} className="rounded-md border bg-card px-4 py-3 sm:px-4" />

                {/* ── Tabs ── */}
                <div ref={tabsRef} className="border-b overflow-x-auto no-scrollbar">
                    <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabKey)}>
                        <TabsList className="bg-transparent h-auto p-0 gap-8 border-none flex justify-start">
                            {TABS.map(t => (
                                <TabsTrigger
                                    key={t.key}
                                    value={t.key}
                                    className="group relative data-[state=active]:text-primary data-[state=active]:shadow-none rounded-none px-0 pb-3 pt-4 bg-transparent whitespace-nowrap gap-1.5 text-muted-foreground hover:text-foreground transition-all border-none"
                                >
                                    {t.icon}
                                    <span className="font-semibold text-sm tracking-tight">{t.label}</span>
                                    {activeTab === t.key && (
                                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary animate-in fade-in slide-in-from-bottom-1" />
                                    )}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                </div>

                {/* ── Panel ── */}
                <div className="analytics-content">
                    {panelMap[activeTab]()}
                </div>
            </div>

            <AnalyticsAdvancedFilters
                open={filterDrawerOpen}
                onOpenChange={setFilterDrawerOpen}
                fields={tabFilterFields}
                tabLabel={TABS.find((t) => t.key === activeTab)?.label ?? 'Analytics'}
                draft={filterDraft}
                onDraftChange={setFilterDraft}
                onApply={() => setTabFilters(filterDraft)}
                onClear={clearFilters}
                departmentOptions={masters.departments}
                locationOptions={masters.locations}
                categoryOptions={masters.categories}
                brandOptions={masters.brands}
                vendorOptions={masters.vendors}
                // Only statuses actually present in the data — an empty option
                // would filter every chart down to nothing.
                statusOptions={(assets?.byStatus ?? []).map((s) => s.status)}
            />

            <KpiDetailModal
                kpiId={activeKpiId}
                onClose={() => { setActiveKpiId(null); setWarrantyBucket(undefined); setUsefulLifeBucket(undefined); }}
                filters={
                    activeKpiId === 'kpi-warranty-status' && warrantyBucket ? { ...activeFilters, warrantyBucket }
                    : activeKpiId === 'kpi-useful-life' && usefulLifeBucket ? { ...activeFilters, usefulLifeBucket }
                    : activeFilters
                }
            />
        </>
    );
};

/* ══════════════════════ SCOPED CSS ══════════════════════ */
/* Kept only for elements with dynamic/semantic coloring (progress fills, trend
   badges, mini-bar, compliance badge) that don't map cleanly onto static
   design-system tokens. */
const CSS = `
/* Loading */
.analytics-loading {
    min-height: 60vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    font-size: 14px;
}
.animate-spin { animation: spin .8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Content */
.analytics-panel { display: flex; flex-direction: column; gap: 20px; }

/* KPI card — icon chip sits with the value on the top row (number reads
   first), label names it small underneath, sub sits smallest at the bottom.
   Matches the StatCard tile pattern used on Dashboard / Cloud Assets. */
.analytics-kpi-top { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.analytics-kpi-icon {
    width: 36px; height: 36px;
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
}
.analytics-trend {
    display: flex; align-items: center; gap: 2px;
    font-size: 11px; font-weight: 700;
    border-radius: 6px; padding: 3px 7px;
    margin-left: auto;
    flex-shrink: 0;
}
.trend-up { background: hsl(var(--success) / 0.12); color: hsl(var(--success)); }
.trend-down { background: hsl(var(--destructive) / 0.10); color: hsl(var(--destructive)); }
.analytics-kpi-value {
    font-size: 20px; font-weight: 800;
    font-variant-numeric: tabular-nums; line-height: 1.1;
    margin: 0;
}
.analytics-kpi-label { font-size: 12px; font-weight: 600; margin: 0; }
.analytics-kpi-sub  { font-size: 11px; margin: 2px 0 0; }

/* Section head — title stacked over its note, both left-aligned. Was a
   row layout (align-items: center) from before the title/note stack; left
   over after the JSX moved to flex-col, "center" here was centering the
   cross axis of a column flex box, i.e. horizontally, not vertically. */
.analytics-section-head {
    display: flex; align-items: flex-start; justify-content: flex-start;
    margin-bottom: 14px;
}
.analytics-section-title { font-size: 13px; font-weight: 700; }
.analytics-section-note  { font-size: 11px; }

/* Progress */
.analytics-progress-row { margin-bottom: 14px; }
.analytics-progress-label {
    display: flex; justify-content: space-between;
    font-size: 12px; font-weight: 600; color: hsl(var(--muted-foreground));
    margin-bottom: 6px;
}
.analytics-progress-count { color: hsl(var(--foreground)); }
.analytics-progress-track {
    height: 8px; background: hsl(var(--muted)); border-radius: 99px; overflow: hidden;
}
.analytics-progress-fill {
    height: 100%; border-radius: 99px;
    transition: width .6s cubic-bezier(.4,0,.2,1);
}

/* Table helpers */
.status-dot {
    display: inline-block; width: 8px; height: 8px;
    border-radius: 50%; margin-right: 8px;
}
.mini-bar-track {
    height: 6px; background: hsl(var(--muted)); border-radius: 99px; overflow: hidden;
}
.mini-bar-fill {
    height: 100%; border-radius: 99px; transition: width .5s;
}

/* Executive tab — partial-data banner */
.analytics-partial-banner {
    display: flex; align-items: center; gap: 8px;
    background: hsl(var(--warning) / 0.10); color: hsl(var(--warning));
    border: 1px solid hsl(var(--warning) / 0.25); border-radius: 8px;
    padding: 10px 14px; font-size: 12px; font-weight: 600;
}

/* Executive tab — week-over-week trend strip */
.analytics-trend-strip-label { font-size: 11px; font-weight: 700; color: hsl(var(--muted-foreground)); margin: 0 0 4px; }
.analytics-trend-inline { display: flex; align-items: center; gap: 3px; font-weight: 700; }
.analytics-trend-unavailable { font-style: italic; }

/* Executive tab — risk queue dots */
.risk-dot {
    display: inline-block; width: 8px; height: 8px;
    border-radius: 50%; margin-right: 8px;
}
.risk-critical { background: #e11d48; }
.risk-warning  { background: #d97706; }
.risk-info     { background: #4f46e5; }

/* License compliance */
.license-compliance-badge {
    margin-top: 20px; padding: 14px 16px;
    background: hsl(var(--muted)); border-radius: 10px;
    display: flex; align-items: center; justify-content: space-between;
}
.compliance-label { font-size: 12px; font-weight: 600; color: hsl(var(--muted-foreground)); }
.compliance-score {
    font-size: 22px; font-weight: 800;
    font-variant-numeric: tabular-nums;
}
.compliance-score.good { color: hsl(var(--success)); }
.compliance-score.warn { color: hsl(var(--destructive)); }
`;

export default AnalyticsDashboard;
